/**
 * VideoGeneratorNode (Canvas V2)
 *
 * Generate video clips from reference images and motion prompts.
 * Features:
 * - Duration slider (3-15s Kling, fixed 8s Veo 3.1)
 * - Motion prompt dual-mode (read: timestamp pills, camera badges,
 *   dialogue cards; edit: textarea)
 * - Inline video preview with hover play, controls on hover
 * - Toolbar: Generate, Expand, Download, Duplicate, Delete
 * - UnifiedBottomPanel (Config, Variants, Audio Remix)
 * - Video lightbox (portal, z-9999)
 * - History with inline players, pending spinners
 * - Model-specific duration snapping
 * - Audio remix UI (TTS provider, voice profiles, segment rows, save changes)
 * - 6 connection handles at specified positions
 */

import { memo, useState, useEffect, useCallback, useMemo, useRef, useId, forwardRef, useImperativeHandle, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Handle, Position, NodeProps, useEdges, useReactFlow } from 'reactflow'
import { executeNode, cancelNodeExecution, InsufficientSparksError, getAssetUrl, getThumbnailUrl, createDebug, createNadDebug, getTypedNodeData, type VideoGeneratorNodeData } from '@ponpon/api-client'
import { useAuthenticatedMediaUrl } from '../../../../hooks/useAuthenticatedMediaUrl'
import type { ReactFlowCanvasEdgeData, ReactFlowCanvasNode, ReactFlowNodeData } from '../../../../hooks/useYCanvas'
import { useCanvasOptional } from '../../CanvasContext'
import type { NodeDataValue } from '../../nodeRegistry'
import { sanitizeNodeError } from '../../../../utils/sanitizeNodeError'
import { SlidersHorizontal, LayoutGrid, AudioWaveform, MessageSquarePlus, BookmarkPlus, RefreshCw } from 'lucide-react'
import {
  AgentNodeToolbar,
  FloatingNodeLabel,
  NODE_TYPE_ICON,
  useNodeActions,
  useBottomPanelAutoOpen,
  UnifiedBottomPanel,
  ReferenceSelector,
  RichTextBox,
  CREATIVE_TEXT_MAX_LENGTH,
  logCreativeTextLimitConfigured,
  CameraCommandsPanel,
  VideoPreviewPlayer,
  ASPECT_RATIO_MAP,
  ASPECT_WIDTH_MAP,
  snapNaturalDimensionsToDisplayDimensions,
  type RichTextBoxHandle,
  type RichTextBoxState,
  type ToolbarAction,
  type TabDefinition,
  type VideoHistoryEntry,
  type SelectedReference,
  type SnappedDisplayDimensions,
  buildAssetReferencesFromEdges,
  buildImageReferenceEdgeUpdates,
  buildCandidates,
  VoiceProfilesPanel,
  getVariantLabel,
  type MentionCandidate,
} from '../shared'
import { useUploadFile } from '../../../../contexts/UploadContext'
import { useWaveform, WaveformBars } from '../shared/AudioRemixShared'
import { useAudioMixer, type MixerClip } from './useAudioMixer'
import {
  getSelectedTrackIndex,
  normalizeTrackSelection,
  resolveTrackSourceDurationS,
  selectTrackExclusive,
  shouldDuckOriginalForTrack,
} from './audioRemixSelection'
import { VideoConfigTabContent } from '../shared/VideoConfigTabContent'
import { VideoVariantsTabContent } from '../shared/VideoVariantsTabContent'
import { snapOnResolutionChange } from '../shared/videoConstraints'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useClickLock } from '../../../../hooks/useClickLock'
import { useShowInsufficientSparks } from '../../../../contexts/InsufficientSparksContext'

import { buildClipDownloadFilename } from '../../../../utils/buildDownloadFilename'
import { parseScript, serializeScript, type ScriptBlock } from './scriptParser'
import { ViewToggle, type ViewMode } from './scriptParts/ViewToggle'
import { ScriptBlockView } from './scriptParts/ScriptBlockView'
import { ScriptBlockEditor } from './scriptParts/ScriptBlockEditor'
import { AddBlockButton } from './scriptParts/AddBlockButton'
import { TimeRuler, PIXELS_PER_SECOND } from './scriptParts/TimeRuler'

type DialogueEntry = NonNullable<VideoGeneratorNodeData['in_dialogue_prompt']>[number]
type VoiceProfile = NonNullable<VideoGeneratorNodeData['in_voice_profiles']>[number]
type ConcreteVoiceProfile = Exclude<VoiceProfile, null>

function isConcreteVoiceProfile(profile: VoiceProfile): profile is ConcreteVoiceProfile {
  return profile !== null
}

function isAudioRemixHistoryEntry(entry: VideoHistoryEntry | undefined): boolean {
  const metadata = entry?.metadata
  return !!metadata && 'type' in metadata && metadata.type === 'audio_remix'
}

/* ── Debug logging ── */
const audioRemixLog = createDebug('dw:audio-remix')
const videoLoadLog = createDebug('dw:audio-remix:video-load')
const voiceProfileSourceLog = createNadDebug('dw:voice-profile-source:data')
const avScriptLog = createNadDebug('dw:av-script-format')

/* ── Types ── */

import {
  nextBlockId,
  type TtsSegment,
  type AudioBlock,
  type AudioSegmentGroup,
  type AudioRemixConfig,
  type AudioRemixSourceOption,
  type EdgeDragMode,
  type MixerRowData,
} from './audioRemixTypes'
import { getCharacterColor } from './audioRemixColors'
import { MixerHeader } from './MixerHeader'
import { MixerVideoStrip } from './MixerVideoStrip'
import { MixerRuler } from './MixerRuler'
import { MixerTrackRow } from './MixerTrackRow'
import { MixerFooter } from './MixerFooter'

export type { VideoGeneratorNodeData }

/** Video Generator: Config, Variants, Audio. Audio tab owns dialogue editing. */
const useVideoTabs = (): TabDefinition[] => {
  return useMemo((): TabDefinition[] => [
    { id: 'config', label: 'Video Config', icon: SlidersHorizontal },
    { id: 'variants', label: 'Variants', icon: LayoutGrid },
    { id: 'audio', label: 'Audio Remix', icon: AudioWaveform },
  ], [])
}

/* ── Motion Prompt Parser ──
 * Pure parser code lives in `./motionPromptParser`.  Re-exported here so
 * existing call sites + the `index.ts` barrel keep working without
 * touching every importer.
 */
export {
  parseMotionPrompt,
  parseTimestampedSegments,
  parseStartTime,
} from './motionPromptParser'
export type { MotionSegment, ParsedMotionPrompt } from './motionPromptParser'

import {
  parseMotionPrompt,
  type ParsedMotionPrompt,
} from './motionPromptParser'

function dialogueEntriesFromPrompt(
  parsed: ParsedMotionPrompt,
  clipIndex: number,
): DialogueEntry[] {
  return parsed.segments
    .filter((seg) => seg.type === 'dialogue' || seg.type === 'voiceover')
    .filter((seg) => Boolean(seg.character?.trim() && seg.action.trim()))
    .map((seg) => {
      const character = seg.character!.trim()
      const entry: DialogueEntry = {
        clip_index: clipIndex,
        voice_character: character,
        voice_text: seg.emotion?.trim()
          ? `[${seg.emotion.trim()}] ${seg.action.trim()}`
          : seg.action.trim(),
        start_s: seg.startTime,
        end_s: seg.endTime,
        type: seg.type,
      }
      if (seg.type === 'voiceover') {
        entry.voice_role_id = character
      }
      return entry
    })
}

function splitVoiceText(voiceText: string): { text: string; emotion: string | null } {
  const match = voiceText.match(/^\[([^\]]+)\]\s*/)
  if (!match) return { text: voiceText, emotion: null }
  return {
    text: voiceText.slice(match[0].length).trim(),
    emotion: match[1].trim(),
  }
}

/* ── Audio Remix Modal — multi-track editor with real-time preview ── */

const AudioEditorModal = memo(function AudioEditorModal({
  projectId,
  videoNodeName,
  origVideoUrl,
  sourceOptions,
  selectedSourceIndex,
  videoDuration,
  segments,
  dialogueEntries,
  config: externalConfig,
  baseline: _baseline,
  isSavingRemix,
  onConfigChange,
  onSave,
  onClose,
  onRerunSTT: _onRerunSTT,
  onGenerateDialogue,
}: {
  projectId: string
  videoNodeName?: string | null
  origVideoUrl: string | null
  sourceOptions: AudioRemixSourceOption[]
  selectedSourceIndex: number | null
  videoDuration: number
  segments: TtsSegment[]
  dialogueEntries: DialogueEntry[] | null
  config: AudioRemixConfig | null
  baseline: AudioRemixConfig | null
  isSavingRemix: boolean
  onConfigChange: (config: AudioRemixConfig) => void
  onSave: (config: AudioRemixConfig) => void
  onClose: () => void
  onRerunSTT: () => void
  onGenerateDialogue: () => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const audioRefs = useRef<Map<string, HTMLAudioElement>>(new Map())
  const [playheadTime, setPlayheadTime] = useState(0)
  const [videoLoaded, setVideoLoaded] = useState(false)
  const modalOpenT0 = useRef(performance.now())
  const markVideoReady = useCallback((evt: string) => {
    videoLoadLog('video-%s dt=%.0fms', evt, performance.now() - modalOpenT0.current)
    setVideoLoaded(true)
  }, [])
  const trimmedVideoNodeName = videoNodeName?.trim()
  const modalTitle = trimmedVideoNodeName ? `Audio Remix (${trimmedVideoNodeName})` : 'Audio Remix'
  const defaultSourceIndex = selectedSourceIndex ?? sourceOptions[sourceOptions.length - 1]?.index ?? null
  const [sourceOriginalIndex, setSourceOriginalIndex] = useState<number | null>(defaultSourceIndex)
  useEffect(() => {
    setSourceOriginalIndex(defaultSourceIndex)
  }, [defaultSourceIndex])
  const selectedSourceOption = useMemo(
    () => sourceOptions.find((option) => option.index === sourceOriginalIndex) ?? sourceOptions[sourceOptions.length - 1] ?? null,
    [sourceOptions, sourceOriginalIndex],
  )
  const activeOrigVideoUrl = selectedSourceOption?.url ?? origVideoUrl
  const handleSourceOriginalChange = useCallback((index: number) => {
    setSourceOriginalIndex(index)
  }, [])

  useEffect(() => {
    videoLoadLog(
      'mount origVideoUrl=%s segments=%d',
      activeOrigVideoUrl ? activeOrigVideoUrl.slice(0, 120) : 'null',
      segments.length,
    )
  }, [activeOrigVideoUrl, segments.length])

  // Web Audio API mixer — routes video audio through GainNode,
  // overlays TTS clips via AudioBufferSourceNode per-track.
  const mixer = useAudioMixer(videoRef)

  // ── Playhead RAF ──
  const rafRef = useRef<number>(0)
  useEffect(() => {
    const tick = () => {
      const video = videoRef.current
      if (video) setPlayheadTime(video.currentTime)
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  // ── Track state ──
  // Build audio URL for a segment version
  const getVersionUrl = useCallback((segIdx: number, versionLabel: string) => {
    const seg = segments[segIdx]
    if (!seg) return null
    const versions = seg.versions && seg.versions.length > 0
      ? seg.versions
      : [
          ...(seg.original_path ? [{ label: 'orig', path: seg.original_path }] : []),
          ...(seg.path ? [{ label: 'v1', path: seg.path }] : []),
        ]
    const v = versions.find((ver) => ver.label === versionLabel)
    return v?.path ? getAssetUrl(projectId, v.path) : null
  }, [segments, projectId])

  // Build groups from saved config or TTS segments (default)
  const buildGroupsFromConfig = useCallback((cfg: AudioRemixConfig | null): AudioSegmentGroup[] => {
    if (cfg?.groups?.length) {
      const voiceoverCount = cfg.groups.filter(g => g.type === 'voiceover').length
      audioRemixLog('buildGroupsFromConfig: from saved config, groups=%d voiceover=%d', cfg.groups.length, voiceoverCount)
      const resolveSavedGroupType = (
        group: AudioRemixConfig['groups'][number],
      ): AudioSegmentGroup['type'] => {
        const sourceTrack = group.tracks.find((track) => track.sourceSegmentIdx != null)
        const sourceSegment = sourceTrack?.sourceSegmentIdx != null
          ? segments[sourceTrack.sourceSegmentIdx]
          : undefined
        if (sourceSegment?.type === 'voiceover' || sourceSegment?.status === 'voiceover') {
          return 'voiceover'
        }
        if (sourceSegment?.type === 'dialogue' || sourceSegment?.path) {
          return 'dialogue'
        }
        return group.type
      }
      return cfg.groups.map((g) => {
        const normalizedType = resolveSavedGroupType(g)
        if (g.type && normalizedType && g.type !== normalizedType) {
          audioRemixLog(
            'buildGroupsFromConfig: correcting saved group type for group=%s from=%s to=%s',
            g.character,
            g.type,
            normalizedType,
          )
        }
        // Filter out stale orig tracks from dropped groups.
        // Voiceover/narrator source audio is replaceable just like dialogue.
        const isDroppedGroup = g.status === 'dropped'
        const filteredTracks = isDroppedGroup
          ? g.tracks.filter(t => (t.sourceVersionLabel ?? t.label) !== 'orig')
          : g.tracks
        const normalizedTracks = normalizeTrackSelection(filteredTracks)
        const selectionChanged = filteredTracks.some(
          (track, index) => track.highlighted !== normalizedTracks[index]?.highlighted,
        )
        if (selectionChanged) {
          audioRemixLog(
            'buildGroupsFromConfig: normalizing saved selection for group=%s',
            g.character,
          )
        }
        return {
          ...g,
          text: g.text ?? '',
          type: normalizedType,
          voice_role_id: normalizedType === 'voiceover' ? g.voice_role_id : undefined,
          tracks: normalizedTracks.map((t) => {
            // Resolve audioUrl based on sourceType:
            // - 'clip': custom clip from orig video → use origVideoUrl
            // - 'tts' (or unset): STT/TTS track → resolve via segment versions
            let url: string | null
            if (t.sourceType === 'clip') {
              url = activeOrigVideoUrl
            } else if (t.sourceVersionLabel === 'orig') {
              // STT tracks use the original video URL
              url = activeOrigVideoUrl
            } else if (t.sourceSegmentIdx != null && t.sourceVersionLabel) {
              url = getVersionUrl(t.sourceSegmentIdx, t.sourceVersionLabel)
            } else {
              url = activeOrigVideoUrl
            }
            const sourceSegment = t.sourceSegmentIdx != null ? segments[t.sourceSegmentIdx] : undefined
            return {
              ...t,
              audioUrl: url,
              fadeInS: t.fadeInS ?? 0,
              fadeOutS: t.fadeOutS ?? 0,
              sourceDurationS: resolveTrackSourceDurationS(t, sourceSegment, selectionChanged),
            }
          }),
        }
      }).filter(g => g.tracks.length > 0)
    }
    // Default: build from TTS segments
    audioRemixLog('buildGroupsFromConfig: from TTS segments, count=%d', segments.length)
    return segments.map((seg, i) => {
      // Determine group type from segment status or dialogue entries
      const isVoiceover = seg.status === 'voiceover' || dialogueEntries?.[i]?.type === 'voiceover'
      const isDropped = seg.status === 'dropped'
      const entryType = dialogueEntries?.[i]?.type
      const voiceRoleId = seg.voice_role_id ?? dialogueEntries?.[i]?.voice_role_id
      const groupType: 'dialogue' | 'voiceover' | undefined = isVoiceover ? 'voiceover' : entryType === 'dialogue' ? 'dialogue' : undefined
      const versions = seg.versions && seg.versions.length > 0
        ? seg.versions
        : [
            // Skip orig track only when no source audio matched.
            ...(!isDropped && seg.original_path ? [{ label: 'orig', path: seg.original_path }] : []),
            ...(seg.path ? [{ label: 'v1', path: seg.path }] : []),
          ]
      // Compute source duration from the timeline slot (not TTS audio duration)
      // so that speed starts at 1.0x when the user hasn't resized the track.
      const sourceDurationS = seg.end_s - seg.start_s
      const tracks: AudioBlock[] = versions.map((v) => ({
        id: nextBlockId(),
        label: v.label === 'orig' ? 'STT' : v.label,
        audioUrl: v.path ? getAssetUrl(projectId, v.path) : null,
        startS: seg.start_s,
        endS: seg.end_s,
        highlighted: v.label !== 'orig' && versions.findIndex(vv => vv.label !== 'orig') === versions.indexOf(v),
        fadeInS: 0,
        fadeOutS: 0,
        sourceSegmentIdx: i,
        sourceVersionLabel: v.label,
        sourceDurationS,
        sourceType: 'tts',
      }))
      return {
        id: `seg_${i}`,
        character: seg.character,
        voice_role_id: voiceRoleId,
        emotion: seg.emotion,
        text: seg.text,
        status: seg.status,
        type: groupType,
        tracks,
      }
    }).filter(g => g.tracks.length > 0)
  }, [getVersionUrl, activeOrigVideoUrl, segments, projectId, dialogueEntries])

  const [groups, setGroups] = useState<AudioSegmentGroup[]>(() => buildGroupsFromConfig(externalConfig))
  // Music attenuation under TTS dialogue. Default is full mute; finite
  // values preserve more of the original ambience under replacement audio.
  const [duckDepthDb, setDuckDepthDb] = useState<number | null>(
    () => externalConfig?.duckDepthDb ?? null,
  )

  // React to external config changes (e.g. agent modifying via API, or Reset).
  // Skip changes that are echoes of our own writes (detected by fingerprint comparison).
  const lastWrittenConfigRef = useRef<string | null>(null)
  useEffect(() => {
    const externalJson = JSON.stringify(externalConfig)
    if (externalJson === lastWrittenConfigRef.current) {
      // This is an echo of our own write — ignore
      return
    }
    // This is a genuine external change — rebuild groups from it
    lastWrittenConfigRef.current = null
    audioRemixLog('external-config-change: rebuilding groups from external config')
    setGroups(buildGroupsFromConfig(externalConfig))
    setDuckDepthDb(externalConfig?.duckDepthDb ?? null)
  }, [externalConfig, buildGroupsFromConfig])

  useEffect(() => {
    setVideoLoaded(false)
    modalOpenT0.current = performance.now()
    videoLoadLog('url-change readyState=%d', videoRef.current?.readyState ?? -1)
    const video = videoRef.current
    if (video && video.readyState >= 2) {
      videoLoadLog('already-ready dt=0ms')
      setVideoLoaded(true)
    }
  }, [activeOrigVideoUrl])

  // ── Initialize mixer when video is ready + decode all TTS clips ──
  const mixerInitRef = useRef(false)
  useEffect(() => {
    const video = videoRef.current
    if (!video || !activeOrigVideoUrl || mixerInitRef.current) return
    const onCanPlay = () => {
      if (mixerInitRef.current) return
      mixerInitRef.current = true
      videoLoadLog('mixer-init dt=%.0fms', performance.now() - modalOpenT0.current)
      mixer.init()
      let clipCount = 0
      for (const group of groups) {
        for (const track of group.tracks) {
          if (track.audioUrl && track.label !== 'STT') {
            mixer.decodeClip(track.id, track.audioUrl)
            clipCount++
          }
        }
      }
      videoLoadLog('mixer-decode-start clips=%d', clipCount)
    }
    if (video.readyState >= 2) onCanPlay()
    else video.addEventListener('canplay', onCanPlay, { once: true })
    return () => video.removeEventListener('canplay', onCanPlay)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrigVideoUrl])

  // ── Sync mixer clips whenever highlights or positions change ──
  useEffect(() => {
    const clips: MixerClip[] = []
    for (const group of groups) {
      const selectedTrackIndex = getSelectedTrackIndex(group.tracks)
      if (selectedTrackIndex === -1) continue
      const track = group.tracks[selectedTrackIndex]
      if (track.audioUrl && track.label !== 'STT') {
        clips.push({
          id: track.id,
          url: track.audioUrl,
          startS: track.startS,
          endS: track.endS,
          enabled: true,
          sourceDurationS: track.sourceDurationS,
          duck: shouldDuckOriginalForTrack(track),
        })
      }
    }
    mixer.updateClips(clips)
  }, [groups, mixer])

  // Stable ref for onConfigChange so the sync effect doesn't re-fire
  // when the parent creates a new closure on every render.
  const onConfigChangeRef = useRef(onConfigChange)
  onConfigChangeRef.current = onConfigChange

  // Sync config to parent on every change
  useEffect(() => {
    const config: AudioRemixConfig = {
      groups: groups.map((g) => ({
        id: g.id,
        character: g.character,
        voice_role_id: g.voice_role_id,
        emotion: g.emotion,
        text: g.text,
        status: g.status,
        type: g.type,
        tracks: g.tracks.map((t) => ({
          id: t.id,
          label: t.label,
          startS: t.startS,
          endS: t.endS,
          highlighted: t.highlighted,
          fadeInS: t.fadeInS > 0 ? t.fadeInS : undefined,
          fadeOutS: t.fadeOutS > 0 ? t.fadeOutS : undefined,
          sourceSegmentIdx: t.sourceSegmentIdx,
          sourceVersionLabel: t.sourceVersionLabel,
          sourceDurationS: t.sourceDurationS,
          sourceType: t.sourceType,
        })),
      })),
    }
    if (selectedSourceOption) {
      config.sourceOriginalIndex = selectedSourceOption.index
      config.sourceOriginalPath = selectedSourceOption.localPath
    }
    config.duckDepthDb = duckDepthDb
    lastWrittenConfigRef.current = JSON.stringify(config)
    onConfigChangeRef.current(config)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, duckDepthDb, selectedSourceOption])

  // Flat list of all blocks for playback priority
  const allBlocks = useMemo(() => groups.flatMap((g) => g.tracks), [groups])

  // ── Orig track selection ──
  const [origSelStart, setOrigSelStart] = useState<number | null>(null)
  const [origSelEnd, setOrigSelEnd] = useState<number | null>(null)
  const hasOrigSelection = origSelStart !== null && origSelEnd !== null && Math.abs(origSelEnd - origSelStart) > 0.05

  const origBarCount = Math.max(30, Math.round(videoDuration * 33))
  const origWaveformBars = useWaveform(activeOrigVideoUrl, origBarCount)

  // ── Determine which tracks are active at playhead (one per group, all play simultaneously) ──
  const _activeTrackIds = useMemo(() => {
    const ids = new Set<string>()
    for (const group of groups) {
      const selectedTrackIndex = getSelectedTrackIndex(group.tracks)
      if (selectedTrackIndex === -1) continue
      const track = group.tracks[selectedTrackIndex]
      if (track.audioUrl && playheadTime >= track.startS && playheadTime < track.endS) {
        ids.add(track.id)
      }
    }
    return ids
  }, [groups, playheadTime])

  // ── Orig waveform: drag to select region ──
  const _origTrackRef = useRef<HTMLDivElement>(null)
  const seekVideoToFraction = useCallback(
    (frac: number) => {
      const video = videoRef.current
      if (!video || !videoDuration) return
      const t = Math.max(0, Math.min(videoDuration, frac * videoDuration))
      video.currentTime = t
      setPlayheadTime(t)
    },
    [videoDuration],
  )
  const handleOrigMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      const rect = e.currentTarget.getBoundingClientRect()
      const startX = e.clientX
      const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      let dragged = false
      let anchorS = 0
      const handleMouseMove = (ev: MouseEvent) => {
        if (!dragged && Math.abs(ev.clientX - startX) > 3) {
          dragged = true
          anchorS = frac * videoDuration
          setOrigSelStart(anchorS)
          setOrigSelEnd(anchorS)
        }
        if (!dragged) return
        const f = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width))
        const s = f * videoDuration
        setOrigSelStart(Math.min(anchorS, s))
        setOrigSelEnd(Math.max(anchorS, s))
      }
      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        if (!dragged) {
          // Click → seek without clearing any pending selection
          seekVideoToFraction(frac)
        }
      }
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [videoDuration, seekVideoToFraction],
  )

  // ── Add block from orig selection ──
  const handleAddBlock = useCallback(() => {
    if (!hasOrigSelection || !activeOrigVideoUrl) return
    audioRemixLog('add-custom-clip: startS=%.2f endS=%.2f sourceType=clip', origSelStart!, origSelEnd!)
    const newTrack: AudioBlock = {
      id: nextBlockId(),
      label: 'clip',
      audioUrl: activeOrigVideoUrl,
      startS: origSelStart!,
      endS: origSelEnd!,
      highlighted: true,
      fadeInS: 0,
      fadeOutS: 0,
      sourceType: 'clip',
      sourceDurationS: origSelEnd! - origSelStart!,
    }
    // Add as a new single-track group
    setGroups((prev) => [...prev, {
      id: `custom_${newTrack.id}`,
      character: 'Custom clip',
      text: '',
      tracks: [newTrack],
    }])
    setOrigSelStart(null)
    setOrigSelEnd(null)
  }, [hasOrigSelection, activeOrigVideoUrl, origSelStart, origSelEnd])

  // ── Block interactions (update tracks within groups) ──
  const updateTrack = useCallback((trackId: string, updater: (t: AudioBlock) => AudioBlock) => {
    setGroups((prev) => prev.map((g) => ({
      ...g,
      tracks: g.tracks.map((t) => t.id === trackId ? updater(t) : t),
    })))
  }, [])

  const selectTrack = useCallback((groupId: string, trackId: string) => {
    setGroups((prev) => prev.map((group) => {
      if (group.id !== groupId) return group
      const trackIndex = group.tracks.findIndex((track) => track.id === trackId)
      if (trackIndex === -1) return group
      const nextTracks = selectTrackExclusive(group.tracks, trackIndex)
      audioRemixLog(
        'select-track: track=%s label=%s character=%s',
        trackId,
        nextTracks[trackIndex]?.label,
        group.character,
      )
      return { ...group, tracks: nextTracks }
    }))
  }, [])

  const _removeGroup = useCallback((groupId: string) => {
    audioRemixLog('remove-group: groupId=%s', groupId)
    setGroups((prev) => prev.filter((g) => g.id !== groupId))
  }, [])

  const setTrackFade = useCallback((trackId: string, field: 'fadeInS' | 'fadeOutS', value: number) => {
    updateTrack(trackId, (t) => ({ ...t, [field]: Math.max(0, value) }))
  }, [updateTrack])

  const moveBlock = useCallback((trackId: string, newStartS: number) => {
    updateTrack(trackId, (t) => {
      const dur = t.endS - t.startS
      const s = Math.max(0, Math.min(newStartS, videoDuration - dur))
      return { ...t, startS: s, endS: s + dur }
    })
  }, [videoDuration, updateTrack])

  const resizeBlock = useCallback((trackId: string, edge: 'left' | 'right', newS: number) => {
    updateTrack(trackId, (t) => {
      if (edge === 'left') {
        const s = Math.max(0, Math.min(newS, t.endS - 0.1))
        return { ...t, startS: s }
      } else {
        const e = Math.min(videoDuration, Math.max(newS, t.startS + 0.1))
        return { ...t, endS: e }
      }
    })
  }, [videoDuration, updateTrack])

  // Reorder tracks within a group
  const reorderTrackInGroup = useCallback((groupId: string, fromIdx: number, toIdx: number) => {
    setGroups((prev) => prev.map((g) => {
      if (g.id !== groupId) return g
      const tracks = [...g.tracks]
      const [moved] = tracks.splice(fromIdx, 1)
      tracks.splice(toIdx, 0, moved)
      return { ...g, tracks }
    }))
  }, [])

  // Drag reorder for sub-tracks within a group
  const trackDragState = useRef<{ groupId: string; fromIdx: number } | null>(null)
  const trackDragOverRef = useRef<{ groupId: string; idx: number } | null>(null)
  const [_trackDragOverIdx, setTrackDragOverIdx] = useState<{ groupId: string; idx: number } | null>(null)

  const _handleTrackDragStart = useCallback((e: React.MouseEvent, groupId: string, trackIdx: number) => {
    e.preventDefault()
    e.stopPropagation()
    trackDragState.current = { groupId, fromIdx: trackIdx }

    const handleMouseMove = (ev: MouseEvent) => {
      if (!(ev.target instanceof HTMLElement)) return
      const row = ev.target.closest('[data-track-row]')
      if (!row) return
      const rowGroupId = row.getAttribute('data-group-id')
      const rowIdx = parseInt(row.getAttribute('data-track-idx') || '-1', 10)
      if (rowGroupId === groupId && rowIdx >= 0) {
        const val = { groupId, idx: rowIdx }
        trackDragOverRef.current = val
        setTrackDragOverIdx(val)
      }
    }

    const handleMouseUp = () => {
      const over = trackDragOverRef.current
      if (trackDragState.current && over) {
        const { fromIdx } = trackDragState.current
        if (over.groupId === groupId && over.idx !== fromIdx) {
          reorderTrackInGroup(groupId, fromIdx, over.idx)
        }
      }
      trackDragState.current = null
      trackDragOverRef.current = null
      setTrackDragOverIdx(null)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [reorderTrackInGroup])

  // ── Block drag handler ──
  const didDragRef = useRef(false)
  const handleBlockDrag = useCallback(
    (e: React.MouseEvent, blockId: string, mode: 'move' | 'resize-left' | 'resize-right') => {
      e.preventDefault()
      e.stopPropagation()
      const block = allBlocks.find((b) => b.id === blockId)
      if (!block) return
      if (!(e.target instanceof HTMLElement)) return
      const container = e.target.closest('[data-timeline]')
      if (!(container instanceof HTMLElement)) return
      const rect = container.getBoundingClientRect()
      const startX = e.clientX
      const origStartS = block.startS
      const origEndS = block.endS
      didDragRef.current = false

      const handleMouseMove = (ev: MouseEvent) => {
        if (Math.abs(ev.clientX - startX) > 3) didDragRef.current = true
        const pxPerSec = rect.width / Math.max(videoDuration, 0.1)
        const deltaSec = (ev.clientX - startX) / pxPerSec
        if (mode === 'move') {
          moveBlock(blockId, origStartS + deltaSec)
        } else if (mode === 'resize-left') {
          resizeBlock(blockId, 'left', origStartS + deltaSec)
        } else {
          resizeBlock(blockId, 'right', origEndS + deltaSec)
        }
      }
      const handleMouseUp = () => {
        if (didDragRef.current) {
          const updated = allBlocks.find((b) => b.id === blockId)
          audioRemixLog('block-drag-end: track=%s mode=%s startS=%.2f→%.2f endS=%.2f→%.2f', blockId, mode, origStartS, updated?.startS, origEndS, updated?.endS)
        }
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [allBlocks, videoDuration, moveBlock, resizeBlock],
  )

  // ── Reorder groups via drag ──
  const [_dragReorderIdx, setDragReorderIdx] = useState<number | null>(null)
  const [_dragOverIdx, setDragOverIdx] = useState<number | null>(null)
  const handleReorder = useCallback((dragIdx: number, dropIdx: number) => {
    audioRemixLog('reorder-group: from=%d to=%d', dragIdx, dropIdx)
    setGroups((prev) => {
      const next = [...prev]
      const [moved] = next.splice(dragIdx, 1)
      next.splice(dropIdx, 0, moved)
      return next
    })
  }, [])

  const _handleRowDragStart = useCallback((e: React.MouseEvent, idx: number) => {
    e.preventDefault()
    e.stopPropagation()
    setDragReorderIdx(idx)
    setDragOverIdx(idx)

    const rows = document.querySelectorAll('[data-block-row]')
    const rowCenters = Array.from(rows).map((r) => {
      const rect = r.getBoundingClientRect()
      return rect.top + rect.height / 2
    })

    const handleMouseMove = (ev: MouseEvent) => {
      // Find which row the cursor is over
      let closest = idx
      let minDist = Infinity
      rowCenters.forEach((center, i) => {
        const dist = Math.abs(ev.clientY - center)
        if (dist < minDist) {
          minDist = dist
          closest = i
        }
      })
      setDragOverIdx(closest)
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      setDragReorderIdx((fromIdx) => {
        setDragOverIdx((toIdx) => {
          if (fromIdx !== null && toIdx !== null && fromIdx !== toIdx) {
            handleReorder(fromIdx, toIdx)
          }
          return null
        })
        return null
      })
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [handleReorder])

  const _playheadPct = videoDuration > 0 ? (playheadTime / videoDuration) * 100 : 0

  // ── Per-track solo playback ──
  const [soloTrackId, setSoloTrackId] = useState<string | null>(null)
  const [mutedIds, setMutedIds] = useState<Record<string, boolean>>({})

  // ── Re-sync mixer clips when mute/solo state changes ──
  useEffect(() => {
    const clips: MixerClip[] = []
    for (const group of groups) {
      const selectedTrackIndex = getSelectedTrackIndex(group.tracks)
      if (selectedTrackIndex === -1) continue
      const track = group.tracks[selectedTrackIndex]
      if (track.audioUrl && track.label !== 'STT') {
        const isEnabled = soloTrackId
          ? group.id === soloTrackId
          : !mutedIds[group.id]
        clips.push({
          id: track.id,
          url: track.audioUrl,
          startS: track.startS,
          endS: track.endS,
          enabled: isEnabled,
          sourceDurationS: track.sourceDurationS,
          duck: shouldDuckOriginalForTrack(track),
        })
      }
    }
    audioRemixLog('clip-sync: total=%d enabled=%d soloTrackId=%s mutedCount=%d',
      clips.length, clips.filter(c => c.enabled).length, soloTrackId ?? 'none', Object.values(mutedIds).filter(Boolean).length)
    mixer.updateClips(clips)
  }, [groups, mixer, mutedIds, soloTrackId])
  const soloTimerRef = useRef<number>(0)
  const soloVideoAudioStateRef = useRef<{ muted: boolean; volume: number } | null>(null)
  const silenceVideoForSoloPreview = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    if (!soloVideoAudioStateRef.current) {
      soloVideoAudioStateRef.current = {
        muted: video.muted,
        volume: video.volume,
      }
    }
    video.pause()
    video.muted = true
    video.volume = 0
    isVideoPlaying.current = false
  }, [])
  const restoreVideoAfterSoloPreview = useCallback(() => {
    const video = videoRef.current
    const priorState = soloVideoAudioStateRef.current
    soloVideoAudioStateRef.current = null
    if (!video || !priorState) return
    video.muted = priorState.muted
    video.volume = priorState.volume
  }, [])
  const handleSoloPlay = useCallback((trackId: string) => {
    const track = allBlocks.find((b) => b.id === trackId)
    const group = groups.find((g) => g.tracks.some((t) => t.id === trackId))
    audioRemixLog('solo-play: track=%s label=%s character=%s range=%.1f-%.1fs url=%s', trackId, track?.label, group?.character, track?.startS, track?.endS, track?.audioUrl)
    const audio = audioRefs.current.get(trackId)
    if (!audio) return
    // Stop current solo
    if (soloTrackId === trackId && !audio.paused) {
      audioRemixLog('solo-stop: track=%s', trackId)
      audio.pause()
      setSoloTrackId(null)
      clearInterval(soloTimerRef.current)
      restoreVideoAfterSoloPreview()
      return
    }
    // Stop any other audio
    audioRefs.current.forEach((a) => { if (!a.paused) a.pause() })
    clearInterval(soloTimerRef.current)
    silenceVideoForSoloPreview()

    // Find the block to get its time bounds
    const block = allBlocks.find((b) => b.id === trackId)
    if (!block) {
      restoreVideoAfterSoloPreview()
      return
    }

    // For TTS clips (v1, v2), the audio file IS the segment — play from 0
    // For orig/clip tracks sourced from the full video, seek to startS
    const isFullVideo = block.audioUrl === activeOrigVideoUrl
    audio.currentTime = isFullVideo ? block.startS : 0
    audio.play().catch(() => {
      setSoloTrackId(null)
      clearInterval(soloTimerRef.current)
      restoreVideoAfterSoloPreview()
    })
    setSoloTrackId(trackId)

    // Poll to stop at endS (for full-video sources) or on natural end
    // For TTS clips, use sourceDurationS (actual audio length) if available,
    // since the clip may be longer than the display window (endS - startS)
    const displayDur = block.endS - block.startS
    const stopTime = isFullVideo ? block.endS : (block.sourceDurationS ?? displayDur)
    soloTimerRef.current = window.setInterval(() => {
      if (audio.currentTime >= stopTime || audio.paused) {
        audio.pause()
        setSoloTrackId(null)
        clearInterval(soloTimerRef.current)
        restoreVideoAfterSoloPreview()
      }
    }, 50)

    const onEnded = () => {
      setSoloTrackId(null)
      clearInterval(soloTimerRef.current)
      restoreVideoAfterSoloPreview()
      audio.removeEventListener('ended', onEnded)
    }
    audio.addEventListener('ended', onEnded)
  }, [soloTrackId, allBlocks, activeOrigVideoUrl, groups, restoreVideoAfterSoloPreview, silenceVideoForSoloPreview])

  useEffect(() => {
    const trackedAudioRefs = audioRefs.current
    return () => {
      clearInterval(soloTimerRef.current)
      trackedAudioRefs.forEach((audio) => audio.pause())
      restoreVideoAfterSoloPreview()
    }
  }, [restoreVideoAfterSoloPreview])

  // ── Edge drag mode ──
  const [edgeDragMode, setEdgeDragMode] = useState<EdgeDragMode>('clip')

  // ── Rerun STT two-click confirm ──
  const [_sttConfirm, setSttConfirm] = useState(false)
  const _sttTimerRef = useRef<number>(0)

  // In fade mode, edge drag adjusts fade instead of resize
  const handleFadeDrag = useCallback(
    (e: React.MouseEvent, blockId: string, edge: 'left' | 'right') => {
      e.preventDefault()
      e.stopPropagation()
      const block = allBlocks.find((b) => b.id === blockId)
      if (!block) return
      if (!(e.target instanceof HTMLElement)) return
      const container = e.target.closest('[data-timeline]')
      if (!(container instanceof HTMLElement)) return
      const rect = container.getBoundingClientRect()
      const startX = e.clientX
      const origFadeIn = block.fadeInS
      const origFadeOut = block.fadeOutS
      const blockDur = block.endS - block.startS

      const handleMouseMove = (ev: MouseEvent) => {
        const pxPerSec = rect.width / Math.max(videoDuration, 0.1)
        const deltaSec = (ev.clientX - startX) / pxPerSec
        if (edge === 'left') {
          setTrackFade(blockId, 'fadeInS', Math.min(Math.max(0, origFadeIn + deltaSec), blockDur * 0.5))
        } else {
          setTrackFade(blockId, 'fadeOutS', Math.min(Math.max(0, origFadeOut - deltaSec), blockDur * 0.5))
        }
      }
      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [allBlocks, videoDuration, setTrackFade],
  )

  // ── Speed drag handler: edge drag changes startS/endS while keeping sourceDurationS constant ──
  const handleSpeedDrag = useCallback(
    (e: React.MouseEvent, blockId: string, edge: 'left' | 'right') => {
      e.preventDefault()
      e.stopPropagation()
      const block = allBlocks.find((b) => b.id === blockId)
      if (!block) return
      if (!(e.target instanceof HTMLElement)) return
      const container = e.target.closest('[data-timeline]')
      if (!(container instanceof HTMLElement)) return
      const rect = container.getBoundingClientRect()
      const startX = e.clientX
      const origStartS = block.startS
      const origEndS = block.endS

      const handleMouseMove = (ev: MouseEvent) => {
        const pxPerSec = rect.width / Math.max(videoDuration, 0.1)
        const deltaSec = (ev.clientX - startX) / pxPerSec
        if (edge === 'left') {
          const rawStart = origStartS + deltaSec
          const newStart = Math.max(0, Math.min(rawStart, origEndS - 0.1))
          updateTrack(blockId, (t) => ({ ...t, startS: Math.min(newStart, t.endS - 0.1) }))
        } else {
          const rawEnd = origEndS + deltaSec
          const newEnd = Math.min(videoDuration, Math.max(rawEnd, origStartS + 0.1))
          updateTrack(blockId, (t) => ({ ...t, endS: Math.max(newEnd, t.startS + 0.1) }))
        }
      }
      const handleMouseUp = () => {
        const updated = allBlocks.find((b) => b.id === blockId)
        if (updated) {
          const newDur = updated.endS - updated.startS
          const speed = updated.sourceDurationS && newDur > 0 ? updated.sourceDurationS / newDur : 1.0
          audioRemixLog('speed-drag-end: track=%s edge=%s speed=%.2fx', blockId, edge, speed)
        }
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [allBlocks, videoDuration, updateTrack],
  )

  // ── Speed preset handler: sets endS to achieve target speed ──
  const _handleSetSpeed = useCallback((trackId: string, targetSpeed: number) => {
    updateTrack(trackId, (t) => {
      const srcDur = t.sourceDurationS ?? (t.endS - t.startS)
      const newDur = srcDur / targetSpeed
      const newEnd = Math.min(videoDuration, t.startS + newDur)
      audioRemixLog('speed-preset: track=%s speed=%s', trackId, targetSpeed)
      return { ...t, endS: newEnd }
    })
  }, [updateTrack, videoDuration])

  // Suppress TS6133 for handlers that will be wired to MixerTrackRow in the next iteration
  void _activeTrackIds; void _origTrackRef
  void _handleTrackDragStart; void _handleRowDragStart
  void _handleSetSpeed; void _playheadPct
  void _removeGroup; void _sttTimerRef; void setSttConfirm

  // ── Mixer rows ──
  const rows: MixerRowData[] = useMemo(() => {
    const origTrack: AudioBlock = {
      id: '__orig__',
      label: 'video',
      audioUrl: activeOrigVideoUrl,
      startS: 0,
      endS: videoDuration,
      highlighted: true,
      fadeInS: 0,
      fadeOutS: 0,
      sourceDurationS: videoDuration,
    }
    const r: MixerRowData[] = [{
      kind: 'orig',
      group: { id: 'orig', character: 'Orig', text: '', tracks: [origTrack] },
      activeTrack: origTrack,
      versions: [],
      characterColor: getCharacterColor('Orig'),
    }]
    for (const g of groups) {
      const selectedIdx = getSelectedTrackIndex(g.tracks)
      const active = selectedIdx >= 0 ? g.tracks[selectedIdx] : g.tracks[0]
      if (!active) continue
      r.push({
        kind: 'group',
        group: g,
        activeTrack: active,
        versions: g.tracks,
        characterColor: getCharacterColor(g.voice_role_id || g.character, g.type),
      })
    }
    return r
  }, [groups, activeOrigVideoUrl, videoDuration])

  // ── Video transport ──
  const isVideoPlaying = useRef(false)
  const handlePlayVideo = useCallback(() => {
    const v = videoRef.current
    if (v) { v.play(); isVideoPlaying.current = true }
  }, [])
  const handlePauseVideo = useCallback(() => {
    const v = videoRef.current
    if (v) { v.pause(); isVideoPlaying.current = false }
  }, [])
  const handleSeekToStart = useCallback(() => {
    const v = videoRef.current
    if (v) { v.currentTime = 0; setPlayheadTime(0) }
  }, [])
  const handleSeekToEnd = useCallback(() => {
    const v = videoRef.current
    if (v) { v.currentTime = videoDuration; setPlayheadTime(videoDuration) }
  }, [videoDuration])
  const handleSeek = useCallback((t: number) => {
    const v = videoRef.current
    if (v) { v.currentTime = t; setPlayheadTime(t) }
  }, [])

  // ── Undo stack ──
  const undoStack = useRef<AudioSegmentGroup[][]>([])
  const pushUndo = useCallback(() => {
    undoStack.current.push(JSON.parse(JSON.stringify(groups)))
    if (undoStack.current.length > 50) undoStack.current.shift()
  }, [groups])
  const handleUndo = useCallback(() => {
    const prev = undoStack.current.pop()
    if (prev) setGroups(prev)
  }, [])

  const handleReset = useCallback(() => {
    pushUndo()
    audioRemixLog('reset: rebuilding from baseline')
    setGroups(buildGroupsFromConfig(null))
    setDuckDepthDb(null)
    setMutedIds({})
    setSoloTrackId(null)
  }, [pushUndo, buildGroupsFromConfig])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement || e.target instanceof HTMLTextAreaElement) return
      if (e.code === 'Space') {
        e.preventDefault()
        const v = videoRef.current
        if (v) {
          if (v.paused) { v.play(); isVideoPlaying.current = true }
          else { v.pause(); isVideoPlaying.current = false }
        }
      } else if (e.code === 'KeyM') {
        const activeGroup = groups.find((g) => {
          const idx = getSelectedTrackIndex(g.tracks)
          if (idx < 0) return false
          const t = g.tracks[idx]
          return playheadTime >= t.startS && playheadTime < t.endS
        })
        if (activeGroup) setMutedIds((m) => ({ ...m, [activeGroup.id]: !m[activeGroup.id] }))
      } else if (e.code === 'KeyS') {
        const activeGroup = groups.find((g) => {
          const idx = getSelectedTrackIndex(g.tracks)
          if (idx < 0) return false
          const t = g.tracks[idx]
          return playheadTime >= t.startS && playheadTime < t.endS
        })
        if (activeGroup) setSoloTrackId((s) => s === activeGroup.id ? null : activeGroup.id)
      } else if (e.code === 'KeyZ' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        handleUndo()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [groups, playheadTime, handleUndo])

  const handleSaveConfig = useCallback(() => {
    const config: AudioRemixConfig = {
      groups: groups.map((g) => ({
        id: g.id,
        character: g.character,
        voice_role_id: g.voice_role_id,
        emotion: g.emotion,
        text: g.text,
        status: g.status,
        type: g.type,
        tracks: g.tracks.map((t) => ({
          id: t.id,
          label: t.label,
          startS: t.startS,
          endS: t.endS,
          highlighted: t.highlighted,
          fadeInS: t.fadeInS > 0 ? t.fadeInS : undefined,
          fadeOutS: t.fadeOutS > 0 ? t.fadeOutS : undefined,
          sourceSegmentIdx: t.sourceSegmentIdx,
          sourceVersionLabel: t.sourceVersionLabel,
          sourceDurationS: t.sourceDurationS,
          sourceType: t.sourceType,
        })),
      })),
    }
    if (selectedSourceOption) {
      config.sourceOriginalIndex = selectedSourceOption.index
      config.sourceOriginalPath = selectedSourceOption.localPath
    }
    config.duckDepthDb = duckDepthDb
    lastWrittenConfigRef.current = JSON.stringify(config)
    onSave(config)
  }, [groups, duckDepthDb, onSave, selectedSourceOption])

  return (
    <div
      data-testid="audio-editor-backdrop"
      className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        data-testid="audio-editor-modal"
        className="relative w-full max-w-6xl h-[90vh] bg-surface-0 rounded-xl shadow-2xl border border-border flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <MixerHeader
          modalTitle={modalTitle}
          edgeDragMode={edgeDragMode}
          onEdgeDragModeChange={setEdgeDragMode}
          playheadTime={playheadTime}
          videoDuration={videoDuration}
          isPlaying={isVideoPlaying.current}
          isSavingRemix={isSavingRemix}
          hasChanges={true}
          onPlay={handlePlayVideo}
          onPause={handlePauseVideo}
          onSeekToStart={handleSeekToStart}
          onSeekToEnd={handleSeekToEnd}
          onReset={handleReset}
          onSave={handleSaveConfig}
          onClose={onClose}
        />

        <MixerVideoStrip
          videoRef={videoRef}
          origVideoUrl={activeOrigVideoUrl}
          sourceOptions={sourceOptions}
          selectedSourceIndex={selectedSourceOption?.index ?? null}
          onSourceOriginalChange={handleSourceOriginalChange}
          videoLoaded={videoLoaded}
          onVideoLoaded={() => markVideoReady('loadeddata')}
          onVideoError={() => videoLoadLog('video-error')}
          playheadTime={playheadTime}
          groups={groups}
          duckDepthDb={duckDepthDb}
          onDuckDepthDbChange={setDuckDepthDb}
        />

        {/* Scrollable track area */}
        <div className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden min-h-0">
          <MixerRuler
            playheadTime={playheadTime}
            videoDuration={videoDuration}
            onSeek={handleSeek}
          />

          {rows.map((row) => (
            <MixerTrackRow
              key={row.group.id}
              row={row}
              muted={!!mutedIds[row.group.id]}
              solo={soloTrackId === row.group.id}
              edgeDragMode={edgeDragMode}
              playheadTime={playheadTime}
              videoDuration={videoDuration}
              origWaveformBars={origWaveformBars}
              origSelStart={origSelStart}
              origSelEnd={origSelEnd}
              onToggleMute={() => setMutedIds((m) => ({ ...m, [row.group.id]: !m[row.group.id] }))}
              onToggleSolo={() => setSoloTrackId((s) => s === row.group.id ? null : row.group.id)}
              onSelectVersion={(trackId) => selectTrack(row.group.id, trackId)}
              onSoloPlay={handleSoloPlay}
              onBlockMouseDown={(e, trackId, mode) => {
                if (edgeDragMode === 'fade' && mode !== 'move') {
                  audioRemixLog('fade-drag: trackId=%s edge=%s', trackId, mode)
                  handleFadeDrag(e, trackId, mode === 'resize-left' ? 'left' : 'right')
                } else if (edgeDragMode === 'speed' && mode !== 'move') {
                  handleSpeedDrag(e, trackId, mode === 'resize-left' ? 'left' : 'right')
                } else {
                  handleBlockDrag(e, trackId, mode)
                }
              }}
              onOrigMouseDown={row.kind === 'orig' ? handleOrigMouseDown : undefined}
              onAddCustomClip={row.kind === 'orig' ? handleAddBlock : undefined}
              hasOrigSelection={row.kind === 'orig' ? hasOrigSelection : undefined}
            />
          ))}

          {/* Empty state: no TTS segments yet */}
          {segments.length === 0 && (
            <div className="flex-1 flex flex-col items-center justify-center py-12 px-4 text-center" data-testid="audio-remix-empty-state">
              <AudioWaveform className="w-12 h-12 text-foreground/15 mb-4" />
              <p className="text-sm text-foreground/60 mb-1">No dialogue tracks yet</p>
              <p className="text-xs text-foreground/30 mb-6 max-w-sm">
                Generate dialogue from your motion prompt, or select a region on the video waveform above to add a custom clip.
              </p>
              <button
                onClick={onGenerateDialogue}
                className="px-4 py-2 rounded-lg bg-primary/20 text-primary hover:bg-primary/30 transition-colors text-sm font-medium"
                data-testid="generate-dialogue-btn"
              >
                Generate Dialogue &amp; TTS
              </button>
            </div>
          )}
        </div>

        <MixerFooter />

        {/* Hidden audio elements for solo play */}
        {allBlocks.map((block) =>
          block.audioUrl ? (
            <audio
              key={block.id}
              ref={(el) => { if (el) audioRefs.current.set(block.id, el); else audioRefs.current.delete(block.id) }}
              src={block.audioUrl}
              preload="auto"
              style={{ display: 'none' }}
            />
          ) : null,
        )}
      </div>
    </div>
  )
})

AudioEditorModal.displayName = 'AudioEditorModal'

/* ── Block waveform: lightweight bars inside a block ── */

const BlockWaveform = memo(function BlockWaveform({ url, accent, durationS, precomputedBars }: { url: string | null; accent: string; durationS: number; precomputedBars?: number[] }) {
  const barCount = Math.max(10, Math.round(durationS * 33))
  const decodedBars = useWaveform(precomputedBars ? null : url, barCount)
  const bars = precomputedBars ?? decodedBars
  return (
    <WaveformBars
      bars={bars}
      barCount={barCount}
      color={accent}
      className="absolute inset-0 px-0.5 pointer-events-none overflow-hidden"
    />
  )
})

BlockWaveform.displayName = 'BlockWaveform'

/* ── Speed Dropdown: per-clip speed preset selector ── */

const SPEED_PRESETS = [0.5, 0.75, 1, 1.25, 1.5, 2]

const SpeedDropdown = memo(function SpeedDropdown({
  speedRatio,
  onSelect,
}: {
  speedRatio: number
  onSelect: (speed: number) => void
}) {
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close on click outside
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (e.target instanceof globalThis.Node && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div ref={dropdownRef} className="absolute top-0.5 left-1 z-20" data-testid="speed-dropdown">
      <button
        className="px-1 py-0.5 rounded text-[8px] font-bold bg-blue-500/20 text-blue-300 border border-blue-500/30 flex items-center gap-0.5 hover:bg-blue-500/30 transition-colors"
        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
        data-testid="speed-dropdown-trigger"
      >
        <svg className="w-2 h-2" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zM8 4a.75.75 0 0 0-.75.75v2.69L5.47 9.22a.75.75 0 0 0 1.06 1.06l2-2A.75.75 0 0 0 8.75 7.5V4.75A.75.75 0 0 0 8 4z"/>
        </svg>
        {speedRatio.toFixed(1)}x
        <svg className="w-1.5 h-1.5 ml-0.5" viewBox="0 0 8 5" fill="currentColor">
          <path d="M0 0l4 5 4-5z"/>
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-0.5 z-30 bg-surface-0 border border-white/10 rounded shadow-lg overflow-hidden min-w-[60px]">
          {SPEED_PRESETS.map((speed) => (
            <button
              key={speed}
              data-testid={`speed-option-${speed}`}
              className={`block w-full px-2 py-1 text-[9px] text-left transition-colors ${
                Math.abs(speedRatio - speed) < 0.01
                  ? 'bg-blue-500/20 text-blue-300 font-bold'
                  : 'text-white/50 hover:bg-white/10 hover:text-white/80'
              }`}
              onClick={(e) => { e.stopPropagation(); onSelect(speed); setOpen(false) }}
            >
              {speed}x
            </button>
          ))}
        </div>
      )}
    </div>
  )
})

SpeedDropdown.displayName = 'SpeedDropdown'

/* ── Render @[[nodeId]] markers as inline pill chips ── */

function renderTextWithChips(
  text: string,
  candidatesMap: Map<string, MentionCandidate>,
  projectId: string,
): ReactNode[] {
  const MARKER_RE = /@\[\[([^\]]+)\]\]/g
  const parts: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let keyIdx = 0

  while ((match = MARKER_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    const nodeId = match[1]
    const candidate = candidatesMap.get(nodeId)

    if (candidate) {
      const thumbUrl = candidate.thumbnailPath
        ? getThumbnailUrl(projectId, candidate.thumbnailPath, 0)
        : null

      parts.push(
        <span
          key={`chip-${keyIdx}`}
          className="inline-flex items-center gap-1 rounded-md border border-primary/25 bg-primary/15 px-1.5 py-0.5 text-xs text-primary align-baseline mx-0.5"
          data-testid={`mention-chip-${nodeId}`}
        >
          {thumbUrl && (
            <img
              src={thumbUrl}
              alt=""
              className="w-3.5 h-3.5 rounded-sm object-cover flex-shrink-0 pointer-events-none"
              onError={(e) => { e.currentTarget.style.display = 'none' }}
            />
          )}
          <span className="inline-flex items-center gap-0.5">
            {NODE_TYPE_ICON[candidate.type] ? (
              <span className="w-3 h-3 text-current opacity-60 flex-shrink-0">{NODE_TYPE_ICON[candidate.type]}</span>
            ) : (
              <span className="text-[11px]">{candidate.icon}</span>
            )}
            <span>{candidate.label}</span>
          </span>
        </span>
      )
    } else {
      parts.push(
        <span
          key={`chip-${keyIdx}`}
          className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-xs text-foreground/40 align-baseline mx-0.5 line-through"
          data-testid={`mention-chip-missing-${nodeId}`}
        >
          <span className="inline-flex items-center gap-0.5">
            <span>{nodeId.slice(0, 8)}...</span>
          </span>
        </span>
      )
    }

    keyIdx++
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  if (parts.length === 0) {
    parts.push(text)
  }

  return parts
}

/* ── Formatted Motion Prompt Display ── */

interface FormattedMotionPromptProps {
  text: string
  nodeId: string
  onEdit: () => void
  onCommitStructured: (newText: string) => void
  canvasNodes: ReactFlowCanvasNode[]
  projectId: string
  /**
   * Fires `true` whenever a structured per-block editor opens, `false` when
   * all per-block editors close. The parent ORs this with its own freeform
   * `isEditingPrompt` flag to decide whether to render the
   * CINEMATOGRAPHY PRESETS panel at the bottom of the Config tab.
   */
  onEditingChange?: (isEditing: boolean) => void
}

/**
 * Imperative handle exposed by `FormattedMotionPrompt` so the parent panel
 * can drive preset insertion in Structured mode (round 4 item 4):
 *  - `getActiveBodyRef()` — returns the open block editor's body
 *    RichTextBoxHandle if one is open, else null.
 *  - `appendVideoBlockFromPreset(preset)` — appends a new video block whose
 *    body is the preset text, then auto-opens the editor on that block.
 */
export interface FormattedMotionPromptHandle {
  getActiveBodyRef: () => RichTextBoxHandle | null
  appendVideoBlockFromPreset: (preset: string) => void
}

const FormattedMotionPrompt = memo(
  forwardRef<FormattedMotionPromptHandle, FormattedMotionPromptProps>(
    function FormattedMotionPrompt(
      { text, nodeId, onEdit, onCommitStructured, canvasNodes, projectId, onEditingChange },
      ref,
    ) {
  const parsed = useMemo(() => parseMotionPrompt(text), [text])
  const structured = useMemo(() => parseScript(text), [text])

  const overlapById = useMemo(() => {
    const set = new Set<string>()
    for (const [a, b] of structured.videoOverlap) {
      set.add(a)
      set.add(b)
    }
    return set
  }, [structured.videoOverlap])

  // Log parse outcome on every text change.
  useEffect(() => {
    avScriptLog({
      event: 'parse-done',
      nodeId,
      textLen: text.length,
      videoCount: structured.videoBlocks.length,
      audioCount: structured.audioBlocks.length,
      hasBgm: structured.disableReason?.reason === 'bgm-present',
      hasLeadingNonSegment: structured.disableReason?.reason === 'leading-non-segment',
      videoOverlap: structured.videoOverlap.length,
      audioOverlapCount: structured.audioOverlapCount,
      canStructured: structured.canStructured,
      disableReason: structured.disableReason?.reason ?? null,
    })
  }, [nodeId, text, structured])

  const [mode, setMode] = useState<ViewMode>(structured.canStructured ? 'structured' : 'freeform')
  const [editingId, setEditingId] = useState<string | null>(null)
  // Round 4 item 4: forward the open block-editor's body RichTextBox handle
  // up to the parent so CINEMATOGRAPHY PRESETS clicks can target the caret.
  const activeBodyRef = useRef<RichTextBoxHandle>(null)
  // After an Add, we won't know the next block's ID until the YDoc round-trips
  // and parseScript reassigns IDs. Pending-edit tracks the (track, startSec)
  // we just added so the editor can re-attach on the next parse.
  const [pendingEdit, setPendingEdit] = useState<
    { track: 'video' | 'audio'; startSec: number } | null
  >(null)

  // Resolve pendingEdit once the new parse contains the newly-added block.
  useEffect(() => {
    if (!pendingEdit) return
    const list = pendingEdit.track === 'video' ? structured.videoBlocks : structured.audioBlocks
    const found = list.find((b) => Math.abs(b.startSec - pendingEdit.startSec) < 0.05)
    if (found) {
      setEditingId(found.id)
      setPendingEdit(null)
    }
  }, [pendingEdit, structured.videoBlocks, structured.audioBlocks])

  // Notify parent whenever per-block editing toggles so the parent can keep
  // the CINEMATOGRAPHY PRESETS panel visible while ANY editor (structured or
  // freeform) is active. We only fire on transitions to avoid a render loop.
  useEffect(() => {
    if (!onEditingChange) return
    onEditingChange(editingId !== null)
  }, [editingId, onEditingChange])

  // Auto-fallback to freeform when structured becomes unavailable.
  useEffect(() => {
    if (!structured.canStructured && mode === 'structured') {
      setMode('freeform')
    }
  }, [structured.canStructured, mode])

  // Generate disabled log fires whenever overlap appears.
  useEffect(() => {
    if (structured.videoOverlap.length > 0) {
      avScriptLog({
        event: 'generate-disabled',
        nodeId,
        reason: 'video-overlap',
        overlapPair: structured.videoOverlap[0],
      })
    }
  }, [nodeId, structured.videoOverlap])

  const candidatesMap = useMemo(() => {
    const candidates = buildCandidates(canvasNodes)
    return new Map(candidates.map(c => [c.id, c]))
  }, [canvasNodes])

  const commitBlocks = useCallback(
    (videoBlocks: ScriptBlock[], audioBlocks: ScriptBlock[]) => {
      const newText = serializeScript({
        clipHeader: structured.clipHeader,
        videoBlocks,
        audioBlocks,
      })
      avScriptLog({
        event: 'serialize-commit',
        nodeId,
        blocksCount: videoBlocks.length + audioBlocks.length,
        textLen: newText.length,
      })
      onCommitStructured(newText)
    },
    [structured.clipHeader, nodeId, onCommitStructured],
  )

  const handleSaveBlock = useCallback(
    (updated: ScriptBlock) => {
      const inVideo = structured.videoBlocks.some((b) => b.id === updated.id)
      const inAudio = structured.audioBlocks.some((b) => b.id === updated.id)
      const nextVideo = inVideo
        ? structured.videoBlocks.map((b) => (b.id === updated.id ? updated : b))
        : structured.videoBlocks
      const nextAudio = inAudio
        ? structured.audioBlocks.map((b) => (b.id === updated.id ? updated : b))
        : structured.audioBlocks
      avScriptLog({
        event: 'block-edit',
        nodeId,
        blockId: updated.id,
        kind: updated.kind,
        startSec: updated.startSec,
        endSec: updated.endSec,
        textLen: updated.text.length,
      })
      setEditingId(null)
      commitBlocks(nextVideo, nextAudio)
    },
    [structured.videoBlocks, structured.audioBlocks, nodeId, commitBlocks],
  )

  const handleDeleteBlock = useCallback(
    (blockId: string, track: 'video' | 'audio') => {
      const kind =
        (track === 'video' ? structured.videoBlocks : structured.audioBlocks).find(
          (b) => b.id === blockId,
        )?.kind ?? 'video'
      avScriptLog({ event: 'block-delete', nodeId, blockId, kind })
      const nextVideo =
        track === 'video'
          ? structured.videoBlocks.filter((b) => b.id !== blockId)
          : structured.videoBlocks
      const nextAudio =
        track === 'audio'
          ? structured.audioBlocks.filter((b) => b.id !== blockId)
          : structured.audioBlocks
      commitBlocks(nextVideo, nextAudio)
    },
    [structured.videoBlocks, structured.audioBlocks, nodeId, commitBlocks],
  )

  const handleTimestampCommit = useCallback(
    (blockId: string, track: 'video' | 'audio', startSec: number, endSec: number) => {
      const update = (blocks: ScriptBlock[]) =>
        blocks.map((b) => (b.id === blockId ? { ...b, startSec, endSec } : b))
      const nextVideo = track === 'video' ? update(structured.videoBlocks) : structured.videoBlocks
      const nextAudio = track === 'audio' ? update(structured.audioBlocks) : structured.audioBlocks
      avScriptLog({
        event: 'block-edit',
        nodeId,
        blockId,
        kind: 'timestamp',
        startSec,
        endSec,
        textLen: 0,
      })
      commitBlocks(nextVideo, nextAudio)
    },
    [structured.videoBlocks, structured.audioBlocks, nodeId, commitBlocks],
  )

  const handleAddBlock = useCallback(
    (track: 'video' | 'audio', initialText = '') => {
      const target = track === 'video' ? structured.videoBlocks : structured.audioBlocks
      const lastEnd = target.reduce((m, b) => Math.max(m, b.endSec), 0)
      const startSec = lastEnd
      const endSec = lastEnd + 1
      // Use crypto.randomUUID when available so the new block has a stable id.
      const id =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `blk-new-${Date.now()}`
      const block: ScriptBlock =
        track === 'video'
          ? { id, kind: 'video', startSec, endSec, text: initialText }
          : { id, kind: 'dialogue', startSec, endSec, text: initialText, character: '', emotion: '' }
      avScriptLog({ event: 'block-add', nodeId, kind: block.kind })
      // Mark this position as pending-edit; once parseScript runs against the
      // new YDoc text, the effect above will resolve the new block's id and
      // open the editor.
      setPendingEdit({ track, startSec })
      if (track === 'video') {
        commitBlocks([...structured.videoBlocks, block], structured.audioBlocks)
      } else {
        commitBlocks(structured.videoBlocks, [...structured.audioBlocks, block])
      }
    },
    [structured.videoBlocks, structured.audioBlocks, nodeId, commitBlocks],
  )

  // Round 4 item 4: expose imperative handle so the parent can route
  // CINEMATOGRAPHY PRESETS clicks into either the open block editor's
  // RichTextBox (preferred) or a new video block when no editor is open.
  useImperativeHandle(
    ref,
    () => ({
      getActiveBodyRef: () => (editingId !== null ? activeBodyRef.current : null),
      appendVideoBlockFromPreset: (preset: string) => {
        // Insert `[Preset]` as the body of a brand-new video block, then
        // auto-open the editor via the existing pendingEdit mechanism.
        handleAddBlock('video', `[${preset}] `)
      },
    }),
    [editingId, handleAddBlock],
  )

  const handleModeChange = useCallback(
    (next: ViewMode) => {
      avScriptLog({
        event: 'mode-toggle',
        nodeId,
        from: mode,
        to: next,
        canStructured: structured.canStructured,
      })
      setMode(next)
    },
    [mode, nodeId, structured.canStructured],
  )

  // Round 4 item 2: revert the round-3 in-flow editor approach for the EDIT
  // path. When `editingId === block.id`, the block's own absolute slot now
  // swaps to the ScriptBlockEditor in the same position. PRESETS overlap is
  // avoided via container `min-height` (audio/video columns flex to fit the
  // editor's natural height — `renderColumnContainer` below).

  const audioColumns: ScriptBlock[][] = useMemo(() => {
    if (structured.audioOverlapCount < 2) {
      return [structured.audioBlocks]
    }
    // Greedy two-column packing: place each block in the first column where
    // its start time is >= the last block's end time.
    const cols: ScriptBlock[][] = [[], []]
    const colEnds = [0, 0]
    for (const b of structured.audioBlocks) {
      let placed = false
      for (let i = 0; i < cols.length; i++) {
        if (b.startSec >= colEnds[i]) {
          cols[i].push(b)
          colEnds[i] = b.endSec
          placed = true
          break
        }
      }
      if (!placed) {
        // Fall back to column 1; overlap is visible.
        cols[1].push(b)
        colEnds[1] = b.endSec
      }
    }
    return cols
  }, [structured.audioBlocks, structured.audioOverlapCount])

  // Compute the timeline span. Cover every block + 1s of padding so blocks
  // never visually clip the bottom edge of the ruler. Minimum height is 4s
  // so the ruler is always visible even when no blocks exist yet.
  const allTimedBlocks = [...structured.videoBlocks, ...structured.audioBlocks]
  const maxEndSec =
    allTimedBlocks.length === 0
      ? 4
      : Math.max(4, Math.ceil(allTimedBlocks.reduce((m, b) => Math.max(m, b.endSec), 0)) + 1)
  const timelineHeightPx = maxEndSec * PIXELS_PER_SECOND

  /** Minimum visible height for a block, so a 0.5s block is still readable.
   * The block keeps its true endSec in the data; only the rendered box can
   * overflow downward by a fraction of a second. */
  const MIN_BLOCK_HEIGHT_PX = 60

  /** Minimum visible height for the in-place EDITOR. The editor needs more
   * room than a read slot so its character/emotion/body inputs are usable
   * even when the source block is very short. The block's true endSec is
   * preserved in data; only the rendered editor box overflows downward. */
  const MIN_EDITOR_HEIGHT_PX = 220

  /** Render one absolutely-positioned block slot on the shared timeline.
   * Round 4 item 2: when the slot is being edited, the slot swaps to the
   * ScriptBlockEditor in the SAME position (round 2 behavior). The slot
   * grows downward to `MIN_EDITOR_HEIGHT_PX` so the editor fits; the column
   * container uses `min-height` so the CINEMATOGRAPHY PRESETS panel below
   * the timeline always has room. */
  const renderTimelineSlot = (block: ScriptBlock, track: 'video' | 'audio') => {
    const top = block.startSec * PIXELS_PER_SECOND
    const naturalHeight = Math.max(0, block.endSec - block.startSec) * PIXELS_PER_SECOND
    const isEditing = editingId === block.id
    const height = isEditing
      ? Math.max(MIN_EDITOR_HEIGHT_PX, naturalHeight)
      : Math.max(MIN_BLOCK_HEIGHT_PX, naturalHeight)
    return (
      <div
        key={block.id}
        className="absolute left-0 right-0 px-0.5"
        style={{
          top,
          height,
          // The editor must render above sibling read slots that may sit at
          // overlapping `top` positions in a multi-column audio packing.
          zIndex: isEditing ? 10 : 1,
        }}
        data-block-id={block.id}
        data-block-kind={block.kind}
        data-block-editing={isEditing ? 'true' : 'false'}
      >
        {isEditing ? (
          <ScriptBlockEditor
            key={block.id}
            block={block}
            onSave={handleSaveBlock}
            onCancel={() => setEditingId(null)}
            onKindToggle={(blockId, from, to) => {
              avScriptLog({
                event: 'block-kind-toggle',
                nodeId,
                blockId,
                from,
                to,
              })
            }}
            canvasNodes={canvasNodes}
            bodyRef={activeBodyRef}
          />
        ) : (
          <ScriptBlockView
            block={block}
            hasOverlap={overlapById.has(block.id)}
            onEdit={() => setEditingId(block.id)}
            onDelete={() => handleDeleteBlock(block.id, track)}
            onTimestampCommit={(s, e) => handleTimestampCommit(block.id, track, s, e)}
            canvasNodes={canvasNodes}
            nodeId={nodeId}
          />
        )}
      </div>
    )
  }

  /** Compute the per-column container's effective height. When an editor is
   * open for a block in this column, grow the container to fit the editor's
   * downward overflow so the AddBlock button and CINEMATOGRAPHY PRESETS are
   * never visually covered (round 4 item 2). */
  const columnHeightFor = (blocks: ScriptBlock[]): number => {
    if (editingId === null) return timelineHeightPx
    const editingBlock = blocks.find((b) => b.id === editingId)
    if (!editingBlock) return timelineHeightPx
    const top = editingBlock.startSec * PIXELS_PER_SECOND
    const naturalHeight = Math.max(0, editingBlock.endSec - editingBlock.startSec) * PIXELS_PER_SECOND
    const editorHeight = Math.max(MIN_EDITOR_HEIGHT_PX, naturalHeight)
    return Math.max(timelineHeightPx, top + editorHeight + 8)
  }

  const disabledTooltip =
    structured.disableReason?.reason === 'bgm-present'
      ? 'BGM lines are not supported in Structured view.'
      : structured.disableReason?.reason === 'leading-non-segment'
        ? `Leading non-segment text: "${structured.disableReason.sample}"`
        : 'Structured view requires timestamped segments.'

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-1 gap-2">
        <label className="text-xs uppercase tracking-wider text-text-muted">Motion Prompt</label>
        <div className="flex items-center gap-1.5">
          <ViewToggle
            mode={mode}
            onChange={handleModeChange}
            disabled={!structured.canStructured}
            disabledTooltip={disabledTooltip}
          />
          {/*
            The overall edit pencil that used to live next to the toggle has
            moved. In Freeform mode it now lives INSIDE the freeform body so
            the user sees the affordance right where editing happens. In
            Structured mode there is no overall pencil at all — users only
            edit per-block via the per-block pencil that appears on hover.
          */}
        </div>
      </div>

      {mode === 'freeform' || !structured.canStructured ? (
        <div className="relative" data-testid="freeform-body">
          {/* In-body pencil (top-right). Always rendered in freeform mode so
              the user can enter the freeform editor regardless of whether
              the prompt parses into segments or falls back to a raw blob. */}
          <button
            onClick={onEdit}
            className="absolute top-0 right-0 w-5 h-5 rounded hover:bg-surface-1 text-text-muted hover:text-foreground flex items-center justify-center transition-colors z-10"
            title="Edit"
            data-testid="motion-prompt-edit-btn"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
          {parsed.fallbackText ? (
            <p className="text-sm text-foreground/80 leading-relaxed pr-7" data-testid="freeform-view">
              {renderTextWithChips(parsed.fallbackText!, candidatesMap, projectId)}
            </p>
          ) : parsed.segments.length === 0 ? (
            <p className="text-sm text-foreground/30 italic pr-7" data-testid="freeform-view">No motion prompt</p>
          ) : (
            <div className="space-y-3 pr-7" data-testid="freeform-view">
              {parsed.clipHeader && (
                <p className="text-sm font-bold text-foreground/90">{parsed.clipHeader}</p>
              )}
              {parsed.segments.map((seg, i) => (
                <div key={i} className="space-y-1" data-testid="motion-segment">
                  {seg.type === 'voiceover' ? (
                    <div className="bg-surface-1 rounded-lg p-2 border border-border-subtle" data-testid="voiceover-card">
                      <span className="text-xxs uppercase tracking-wider text-primary/70 font-medium">voiceover</span>
                      <span className="inline-block text-xs px-1.5 py-0.5 rounded-full bg-primary/20 text-primary/80 font-medium ml-2">
                        {seg.timestamp}
                      </span>
                      {seg.character && (
                        <p className="text-sm font-bold text-foreground/90 mt-1">
                          {seg.character}
                          {seg.emotion && (
                            <span className="text-xxs italic text-foreground/50 ml-1">{seg.emotion}</span>
                          )}
                        </p>
                      )}
                      <p className="text-sm text-foreground/80 leading-relaxed mt-0.5 italic">
                        &ldquo;{renderTextWithChips(seg.action, candidatesMap, projectId)}&rdquo;
                      </p>
                    </div>
                  ) : seg.type === 'dialogue' ? (
                    <div className="bg-surface-1 rounded-lg p-2 border border-border-subtle" data-testid="dialogue-card">
                      <span className="inline-block text-xs px-1.5 py-0.5 rounded-full bg-primary/20 text-primary/80 font-medium">
                        {seg.timestamp}
                      </span>
                      {seg.character && (
                        <p className="text-sm font-bold text-foreground/90 mt-1">
                          {seg.character}
                          {seg.emotion && (
                            <span className="text-xxs italic text-foreground/50 ml-1">{seg.emotion}</span>
                          )}
                        </p>
                      )}
                      <p className="text-sm text-foreground/80 leading-relaxed mt-0.5">
                        &ldquo;{renderTextWithChips(seg.action, candidatesMap, projectId)}&rdquo;
                      </p>
                    </div>
                  ) : (
                    <>
                      <span className="inline-block text-xs px-1.5 py-0.5 rounded-full bg-primary/20 text-primary/80 font-medium">
                        {seg.timestamp}
                      </span>
                      <p className="text-sm text-foreground/80 leading-relaxed">
                        {renderTextWithChips(seg.action, candidatesMap, projectId)}
                      </p>
                      {seg.camera && (
                        <p className="text-sm">
                          <span className="text-text-muted">Camera</span>{' '}
                          <span className="text-foreground/70">{seg.camera}</span>
                        </p>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2" data-testid="structured-view">
          {structured.clipHeader && (
            <p className="text-sm font-bold text-foreground/90">{structured.clipHeader}</p>
          )}
          {/* Shared timeline: ruler on the left, Video column, then the
              Dialogue & SFX column (which may split into two sub-columns when
              audio overlaps). Blocks are absolutely positioned by
              startSec / endSec * PIXELS_PER_SECOND so every block lines up
              with the ruler ticks. */}
          <div className="flex gap-2" data-testid="av-timeline">
            <div className="relative shrink-0" style={{ height: timelineHeightPx }}>
              <TimeRuler maxSec={maxEndSec} />
            </div>
            <div className="flex-1 grid grid-cols-2 gap-2 min-w-0">
              <div className="min-w-0" data-testid="video-column">
                <p className="text-xxs uppercase tracking-wider text-text-muted mb-1.5">Video</p>
                <div className="relative" style={{ height: columnHeightFor(structured.videoBlocks) }}>
                  {structured.videoBlocks.map((b) => renderTimelineSlot(b, 'video'))}
                </div>
                <div className="mt-1.5">
                  <AddBlockButton track="video" onClick={() => handleAddBlock('video')} />
                </div>
              </div>
              <div className="min-w-0" data-testid="audio-column">
                {/* Round 4 item 3: header reads "Dialogue & Sound Effect"; we
                    do NOT render a second header above the second column when
                    audio overlaps split the track into two sub-columns. */}
                <p className="text-xxs uppercase tracking-wider text-text-muted mb-1.5">
                  Dialogue &amp; Sound Effect
                </p>
                {audioColumns.length === 1 ? (
                  <div className="relative" style={{ height: columnHeightFor(audioColumns[0]) }}>
                    {audioColumns[0].map((b) => renderTimelineSlot(b, 'audio'))}
                  </div>
                ) : (
                  <div
                    className="grid grid-cols-2 gap-1.5"
                    data-testid="audio-overlap-columns"
                    style={{ height: Math.max(...audioColumns.map(columnHeightFor)) }}
                  >
                    {audioColumns.map((col, i) => (
                      <div key={i} className="relative">
                        {col.map((b) => renderTimelineSlot(b, 'audio'))}
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-1.5">
                  <AddBlockButton track="audio" onClick={() => handleAddBlock('audio')} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}),
)

FormattedMotionPrompt.displayName = 'FormattedMotionPrompt'

/* ── Add Dialogue Form ── */

const ADD_DIALOGUE_LABEL_CLASS = 'text-xxs uppercase tracking-wider text-text-muted block mb-0.5'
const ADD_DIALOGUE_FIELD_CLASS = 'rounded-lg bg-input-bg border border-input-border px-2 py-1 text-xs text-foreground placeholder:text-text-muted transition-colors hover:bg-surface-0 hover:border-input-border-hover focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'

const parseFiniteTimeInput = (value: string): number | null => {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

const clampTimeToDuration = (value: number, duration: number): number => {
  return Math.min(Math.max(value, 0), duration)
}

const formatPromptTime = (value: number): string => {
  return (Math.round(value * 10) / 10).toFixed(1)
}

export const AddDialogueForm = memo(function AddDialogueForm({
  duration,
  characterNames,
  voiceRoleOptions,
  clipIndex,
  initialEntry,
  submitLabel,
  onAdd,
  onCancel,
}: {
  duration: number
  characterNames: string[]
  voiceRoleOptions: string[]
  clipIndex: number
  initialEntry?: DialogueEntry
  submitLabel?: string
  onAdd: (entry: DialogueEntry) => void
  onCancel: () => void
}) {
  const maxDuration = Number.isFinite(duration) && duration > 0 ? duration : 8
  const initialVoice = splitVoiceText(initialEntry?.voice_text ?? '')
  const characterListId = useId()
  const voiceRoleListId = useId()
  const [type, setType] = useState<'dialogue' | 'voiceover'>(
    initialEntry?.type === 'voiceover' ? 'voiceover' : 'dialogue',
  )
  const [character, setCharacter] = useState(initialEntry?.voice_character ?? characterNames[0] ?? '')
  const [voiceRoleId, setVoiceRoleId] = useState(initialEntry?.voice_role_id ?? '')
  const [startS, setStartS] = useState(formatPromptTime(initialEntry?.start_s ?? 1.0))
  const [endS, setEndS] = useState(formatPromptTime(initialEntry?.end_s ?? Math.min(3.0, maxDuration)))
  const [emotion, setEmotion] = useState(initialVoice.emotion ?? '')
  const [text, setText] = useState(initialVoice.text)
  const characterOptions = useMemo(() => {
    const names = [...characterNames]
    const trimmedCharacter = character.trim()
    if (trimmedCharacter && !names.includes(trimmedCharacter)) {
      names.unshift(trimmedCharacter)
    }
    return names
  }, [character, characterNames])
  const roleOptions = useMemo(() => {
    const names = [...voiceRoleOptions]
    const trimmedRole = voiceRoleId.trim()
    if (trimmedRole && !names.includes(trimmedRole)) {
      names.unshift(trimmedRole)
    }
    return names
  }, [voiceRoleId, voiceRoleOptions])

  useEffect(() => {
    if (!character.trim() && characterNames.length > 0) {
      setCharacter(characterNames[0])
    }
  }, [character, characterNames])

  const timingState = useMemo(() => {
    const parsedStart = parseFiniteTimeInput(startS)
    const parsedEnd = parseFiniteTimeInput(endS)

    if (parsedStart === null || parsedEnd === null) {
      return {
        isValid: false,
        message: 'Enter finite start and end times.',
        start: 0,
        end: 0,
      }
    }

    const clampedStart = clampTimeToDuration(parsedStart, maxDuration)
    const clampedEnd = clampTimeToDuration(parsedEnd, maxDuration)

    if (clampedEnd <= clampedStart) {
      return {
        isValid: false,
        message: 'End time must be after start time.',
        start: clampedStart,
        end: clampedEnd,
      }
    }

    const willClamp = clampedStart !== parsedStart || clampedEnd !== parsedEnd
    return {
      isValid: true,
      message: willClamp
        ? `Will save as ${formatPromptTime(clampedStart)}s-${formatPromptTime(clampedEnd)}s.`
        : '',
      start: clampedStart,
      end: clampedEnd,
    }
  }, [endS, maxDuration, startS])

  const canAdd = Boolean(text.trim() && character.trim() && timingState.isValid)

  const handleAdd = () => {
    const trimmedText = text.trim()
    if (!trimmedText || !character.trim() || !timingState.isValid) return

    const entry: DialogueEntry = {
      clip_index: clipIndex,
      voice_character: character.trim(),
      voice_text: emotion.trim() ? `[${emotion.trim()}] ${trimmedText}` : trimmedText,
      start_s: timingState.start,
      end_s: timingState.end,
      type,
    }
    const trimmedVoiceRoleId = voiceRoleId.trim()
    if (trimmedVoiceRoleId) {
      entry.voice_role_id = trimmedVoiceRoleId
    }
    onAdd(entry)
  }

  return (
    <div className="mt-2 rounded-lg border border-input-border bg-surface-1 p-3 space-y-2" data-testid="add-dialogue-form">
      {/* Type toggle */}
      <div className="flex gap-1">
        <button
          onClick={() => setType('dialogue')}
          className={`h-7 rounded-full border px-2 text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${
            type === 'dialogue'
              ? 'border-primary/50 bg-primary/20 text-primary'
              : 'border-input-border bg-surface-1 text-text-muted hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground'
          }`}
          data-testid="dialogue-type-btn"
        >
          Dialogue
        </button>
        <button
          onClick={() => setType('voiceover')}
          className={`h-7 rounded-full border px-2 text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${
            type === 'voiceover'
              ? 'border-primary/50 bg-primary/20 text-primary'
              : 'border-input-border bg-surface-1 text-text-muted hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground'
          }`}
          data-testid="voiceover-type-btn"
        >
          Voiceover
        </button>
      </div>

      {/* Speaker + narrator voice role */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={ADD_DIALOGUE_LABEL_CLASS}>Speaker</label>
          <input
            value={character}
            onChange={(e) => setCharacter(e.target.value)}
            className={`h-7 w-full ${ADD_DIALOGUE_FIELD_CLASS}`}
            placeholder="Character or narrator"
            list={characterOptions.length > 0 ? characterListId : undefined}
            data-testid="dialogue-character-input"
          />
          {characterOptions.length > 0 && (
            <datalist id={characterListId}>
              {characterOptions.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          )}
        </div>
        <div>
          <label className={ADD_DIALOGUE_LABEL_CLASS}>Voice role <span className="normal-case">(optional)</span></label>
          <input
            value={voiceRoleId}
            onChange={(e) => setVoiceRoleId(e.target.value)}
            className={`h-7 w-full ${ADD_DIALOGUE_FIELD_CLASS}`}
            placeholder={type === 'voiceover' ? 'opening_narrator' : 'role id'}
            list={roleOptions.length > 0 ? voiceRoleListId : undefined}
            data-testid="dialogue-voice-role-input"
          />
          {roleOptions.length > 0 && (
            <datalist id={voiceRoleListId}>
              {roleOptions.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          )}
        </div>
      </div>

      {/* Timing + Emotion row */}
      <div className="flex gap-2">
        <div className="flex-1">
          <label className={ADD_DIALOGUE_LABEL_CLASS}>Timing</label>
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={startS}
              onChange={(e) => setStartS(e.target.value)}
              className={`h-7 w-16 text-center ${ADD_DIALOGUE_FIELD_CLASS}`}
              min={0}
              max={maxDuration}
              step={0.5}
              aria-invalid={!timingState.isValid}
              data-testid="dialogue-start-time"
            />
            <span className="text-text-muted text-xs">–</span>
            <input
              type="number"
              value={endS}
              onChange={(e) => setEndS(e.target.value)}
              className={`h-7 w-16 text-center ${ADD_DIALOGUE_FIELD_CLASS}`}
              min={0}
              max={maxDuration}
              step={0.5}
              aria-invalid={!timingState.isValid}
              data-testid="dialogue-end-time"
            />
            <span className="text-text-muted text-xxs">sec</span>
          </div>
          {!timingState.isValid && (
            <p className="mt-1 text-xxs text-destructive" data-testid="dialogue-timing-error">
              {timingState.message}
            </p>
          )}
          {timingState.isValid && timingState.message && (
            <p className="mt-1 text-xxs text-text-muted" data-testid="dialogue-timing-hint">
              {timingState.message}
            </p>
          )}
        </div>
        <div className="flex-1">
          <label className={ADD_DIALOGUE_LABEL_CLASS}>Emotion <span className="normal-case">(optional)</span></label>
          <input
            value={emotion}
            onChange={(e) => setEmotion(e.target.value)}
            className={`h-7 w-full ${ADD_DIALOGUE_FIELD_CLASS}`}
            placeholder="e.g. whispers, excited"
            data-testid="dialogue-emotion-input"
          />
        </div>
      </div>

      {/* Dialogue text */}
      <div>
        <label className={ADD_DIALOGUE_LABEL_CLASS}>Line</label>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          className={`h-8 w-full ${ADD_DIALOGUE_FIELD_CLASS}`}
          placeholder="What does the character say?"
          data-testid="dialogue-text-input"
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); handleAdd() }
            if (e.key === 'Escape') onCancel()
          }}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="h-7 rounded-lg border border-input-border bg-surface-1 px-2 text-xs text-text-muted transition-colors hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
          data-testid="dialogue-cancel-btn"
        >
          Cancel
        </button>
        <button
          onClick={handleAdd}
          disabled={!canAdd}
          className="h-7 rounded-lg border border-primary/40 bg-primary/20 px-3 text-xs text-primary transition-colors hover:bg-primary/30 hover:border-primary/60 disabled:cursor-not-allowed disabled:border-input-border disabled:bg-surface-1 disabled:text-text-muted disabled:hover:bg-surface-1 disabled:hover:border-input-border focus:outline-none focus:ring-2 focus:ring-primary/20"
          data-testid="dialogue-add-btn"
        >
          {submitLabel ?? `Add ${type === 'voiceover' ? 'Voiceover' : 'Dialogue'}`}
        </button>
      </div>
    </div>
  )
})

export const DialoguePromptPanel = memo(function DialoguePromptPanel({
  entries,
  promptEntries,
  hasStoredDialogue,
  hasIncomingEdge,
  showDialogueForm,
  duration,
  characterNames,
  voiceRoleOptions,
  clipIndex,
  onShowForm,
  onAdd,
  onUpdate,
  onDelete,
  onCancelAdd,
  onResetFromPrompt,
}: {
  entries: DialogueEntry[]
  promptEntries: DialogueEntry[]
  hasStoredDialogue: boolean
  hasIncomingEdge: boolean
  showDialogueForm: boolean
  duration: number
  characterNames: string[]
  voiceRoleOptions: string[]
  clipIndex: number
  onShowForm: () => void
  onAdd: (entry: DialogueEntry) => void
  onUpdate: (index: number, entry: DialogueEntry) => void
  onDelete: (index: number) => void
  onCancelAdd: () => void
  onResetFromPrompt: () => void
}) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  useEffect(() => {
    if (editingIndex !== null && editingIndex >= entries.length) {
      setEditingIndex(null)
    }
  }, [editingIndex, entries.length])

  return (
    <div className="rounded-lg border border-border-subtle bg-surface-0 p-3" data-testid="structured-dialogue-section">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-wider text-text-muted">Dialogue</label>
            <span className="rounded-full border border-input-border bg-surface-1 px-1.5 py-0.5 text-xxs text-text-muted">
              in_dialogue_prompt
            </span>
            {hasIncomingEdge && (
              <span className="rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-xxs text-primary">
                connected
              </span>
            )}
          </div>
          <p className="mt-1 text-xxs text-text-muted">
            Audio remix uses this structured dialogue. Motion-prompt dialogue can be imported with reset.
          </p>
        </div>
        <button
          onClick={() => {
            setEditingIndex(null)
            onResetFromPrompt()
          }}
          disabled={promptEntries.length === 0}
          className="h-7 shrink-0 rounded-lg border border-input-border bg-surface-1 px-2 text-xs text-text-muted transition-colors hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-surface-1 disabled:hover:border-input-border focus:outline-none focus:ring-2 focus:ring-primary/20"
          data-testid="dialogue-reset-from-prompt-btn"
          aria-label="Reset structured dialogue from motion-prompt dialogue tags"
          title={
            promptEntries.length > 0
              ? `Overwrite in_dialogue_prompt from <dialogue>/<voiceover> tags in the motion prompt.${hasIncomingEdge ? ' This also detaches the connected dialogue input.' : ''}`
              : 'No <dialogue> or <voiceover> tags found in the motion prompt.'
          }
        >
          Reset from prompt
        </button>
      </div>

      {hasIncomingEdge && (
        <p className="mt-2 rounded-lg border border-primary/20 bg-primary/10 px-2 py-1 text-xxs text-primary/90" data-testid="dialogue-connected-warning">
          Editing will detach the connected dialogue input so upstream nodes no longer overwrite this clip.
        </p>
      )}

      {promptEntries.length > 0 && hasStoredDialogue && (
        <p className="mt-2 text-xxs text-text-muted" data-testid="dialogue-prompt-ignored-note">
          Motion-prompt dialogue remains visible in Video Config; structured dialogue below is the active audio source.
        </p>
      )}

      <div className="mt-3 space-y-2">
        {entries.length > 0 ? (
          entries.map((entry, i) => {
            const { text, emotion } = splitVoiceText(entry.voice_text)
            const type = entry.type === 'voiceover' ? 'voiceover' : 'dialogue'
            if (editingIndex === i) {
              return (
                <AddDialogueForm
                  key={`edit-${i}`}
                  duration={duration}
                  characterNames={characterNames}
                  voiceRoleOptions={voiceRoleOptions}
                  clipIndex={clipIndex}
                  initialEntry={entry}
                  submitLabel="Save dialogue"
                  onAdd={(updatedEntry) => {
                    onUpdate(i, updatedEntry)
                    setEditingIndex(null)
                  }}
                  onCancel={() => setEditingIndex(null)}
                />
              )
            }

            return (
              <div
                key={`${entry.voice_character}-${entry.start_s}-${entry.end_s}-${i}`}
                className="rounded-lg border border-border-subtle bg-surface-1 p-2"
                data-testid="dialogue-card"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="inline-block rounded-full bg-primary/20 px-1.5 py-0.5 text-xs font-medium text-primary/80">
                      {formatPromptTime(entry.start_s)}s-{formatPromptTime(entry.end_s)}s
                    </span>
                    {type === 'voiceover' && (
                      <span className="text-xxs uppercase tracking-wider text-primary/70">voiceover</span>
                    )}
                    {entry.voice_role_id && (
                      <span className="rounded-full border border-input-border bg-surface-0 px-1.5 py-0.5 text-xxs text-text-muted">
                        {entry.voice_role_id}
                      </span>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        onCancelAdd()
                        setEditingIndex(i)
                      }}
                      className="h-6 rounded-md border border-input-border bg-surface-0 px-1.5 text-xxs text-text-muted transition-colors hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                      data-testid="dialogue-edit-btn"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        onDelete(i)
                        setEditingIndex(null)
                      }}
                      className="h-6 rounded-md border border-input-border bg-surface-0 px-1.5 text-xxs text-text-muted transition-colors hover:bg-destructive/10 hover:border-destructive/50 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-destructive/20"
                      data-testid="dialogue-delete-btn"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <p className="mt-1 text-sm font-bold text-foreground/90">
                  {entry.voice_character}
                  {emotion && (
                    <span className="ml-1 text-xxs italic text-foreground/50">{emotion}</span>
                  )}
                </p>
                <p className={`mt-0.5 text-sm leading-relaxed text-foreground/80 ${type === 'voiceover' ? 'italic' : ''}`}>
                  &ldquo;{text}&rdquo;
                </p>
              </div>
            )
          })
        ) : (
          <p className="rounded-lg border border-border-subtle bg-surface-1 px-2 py-2 text-sm text-text-muted" data-testid="dialogue-empty-state">
            No structured dialogue on this clip.
          </p>
        )}
      </div>

      {showDialogueForm ? (
        <AddDialogueForm
          duration={duration}
          characterNames={characterNames}
          voiceRoleOptions={voiceRoleOptions}
          clipIndex={clipIndex}
          onAdd={onAdd}
          onCancel={onCancelAdd}
        />
      ) : (
        <button
          onClick={() => {
            setEditingIndex(null)
            onShowForm()
          }}
          className="group mt-2 flex h-7 items-center gap-1.5 rounded-lg border border-input-border bg-surface-1 px-2 text-xs text-text-muted transition-colors hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
          data-testid="add-dialogue-btn"
        >
          <MessageSquarePlus className="h-3.5 w-3.5 text-primary/70 transition-colors group-hover:text-primary" />
          Add dialogue or voiceover
        </button>
      )}

      {!hasStoredDialogue && promptEntries.length > 0 && (
        <p className="mt-2 text-xxs text-text-muted" data-testid="dialogue-auto-generated-note">
          Previewing dialogue parsed from motion-prompt tags.
        </p>
      )}
    </div>
  )
})

/* ── Main Component ── */

export const VideoGeneratorNode = memo(function VideoGeneratorNode(
  props: NodeProps<VideoGeneratorNodeData>,
) {
  const { id, data, selected, dragging } = props
  const {
    in_name,
    label,
    out_generated_video,
    in_motion_prompt: prompt = '',
    in_clip_number,
    in_duration: duration = 5,
    in_model: model = 'seedance-2.0-image-to-video',
    in_resolution: resolution = 'auto',
    in_aspect_ratio: aspect_ratio = '16:9',
    in_generate_audio: generate_audio = true,
    in_tts_provider: ttsProvider = 'elevenlabs',
    in_dialogue_mode: dialogueMode = 'tts_remix',
    status = 'idle',
    progressMessage,
    error,
    out_video,
    out_remixed_video,
    out_tts_segments,
    is_inside_group,
  } = data
  const generated_video = out_generated_video
  const projectId = useProjectId()

  const [assetDisplayDimensions, setAssetDisplayDimensions] = useState<SnappedDisplayDimensions | null>(null)
  const requestedAspectRatio = ASPECT_RATIO_MAP[aspect_ratio] ?? '16/9'
  const cssAspectRatio = assetDisplayDimensions?.aspectRatio ?? requestedAspectRatio
  const nodeWidth = assetDisplayDimensions?.width ?? ASPECT_WIDTH_MAP[aspect_ratio] ?? 315

  const videoTabs = useVideoTabs()
  const baseVideoSelectId = useId()
  const hasGeneratedVideo = Boolean(out_generated_video || out_video || out_remixed_video)

  const { setNodes, getNodes, getEdges } = useReactFlow<ReactFlowNodeData, ReactFlowCanvasEdgeData>()
  const edges = useEdges<ReactFlowCanvasEdgeData>()
  const canvas = useCanvasOptional()
  const uploadFile = useUploadFile()
  const { handleDelete, handleDuplicate, handleDownload: downloadFile, handleAddToChat, handleSaveToLibrary } = useNodeActions({ nodeId: id })

  // Video preview hover state
  const [isHovering, setIsHovering] = useState(false)
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const [isAudioEditorOpen, setIsAudioEditorOpen] = useState(false)

  // Escape key dismisses video lightbox or audio editor
  useEffect(() => {
    if (!isVideoModalOpen && !isAudioEditorOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        if (isAudioEditorOpen) setIsAudioEditorOpen(false)
        else setIsVideoModalOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isVideoModalOpen, isAudioEditorOpen])

  // Bottom panel state
  const [bottomPanelTab, setBottomPanelTab] = useState('config')
  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [isEditingPrompt, setIsEditingPrompt] = useState(false)
  // True when any per-block editor inside Structured view is open. We lift
  // this state up from FormattedMotionPrompt so the Config tab can keep the
  // CINEMATOGRAPHY PRESETS panel visible while a structured block editor is
  // active — same UX affordance that already exists for freeform editing.
  const [isStructuredEditing, setIsStructuredEditing] = useState(false)
  const [showDialogueForm, setShowDialogueForm] = useState(false)
  const richTextBoxRef = useRef<RichTextBoxHandle>(null)
  // Round 4 item 4: imperative handle into FormattedMotionPrompt so the
  // parent's preset panel can target either the open block editor's body
  // (insert at caret) or append a new video block (when no editor is open).
  const formattedMotionPromptRef = useRef<FormattedMotionPromptHandle>(null)
  // Round 4 item 6: lightweight undo stack of previous prompt strings so
  // Cmd+Z reverts the last preset insertion (or any other preset-driven
  // write). The stack is scoped to the lifetime of this node; YDoc's own
  // CRDT history is not exposed here.
  const prevPromptStackRef = useRef<string[]>([])
  const [localPrompt, setLocalPrompt] = useState(prompt)
  const isEditingPromptRef = useRef(isEditingPrompt)
  isEditingPromptRef.current = isEditingPrompt
  const localPromptRef = useRef(localPrompt)
  localPromptRef.current = localPrompt
  // Sync local prompt when external data changes
  useEffect(() => {
    if (!isEditingPrompt) setLocalPrompt(prompt)
  }, [prompt, isEditingPrompt])

  useEffect(() => {
    if (isEditingPrompt) {
      logCreativeTextLimitConfigured('video-motion-prompt', { nodeId: id })
    }
  }, [id, isEditingPrompt])

  // Audio remix state
  const [isSavingRemix, setIsSavingRemix] = useState(false)

  // Video duration from the generated video for selection calculations
  const videoDuration = data.out_video_duration || 0

  const parsedPrompt = useMemo(() => parseMotionPrompt(prompt), [prompt])
  const motionScript = useMemo(() => parseScript(prompt), [prompt])
  const motionCanGenerate = motionScript.videoOverlap.length === 0
  const motionGenerateBlockReason = motionCanGenerate
    ? undefined
    : 'Video segments overlap. Fix overlap before generating.'
  const clipIndex = in_clip_number ?? 1
  const promptDialogueEntries = useMemo(
    () => dialogueEntriesFromPrompt(parsedPrompt, clipIndex),
    [clipIndex, parsedPrompt],
  )
  const storedDialogueEntries = Array.isArray(data.in_dialogue_prompt)
    ? data.in_dialogue_prompt
    : null
  const hasStoredDialogue = storedDialogueEntries !== null
  const dialogueEntriesForDisplay = storedDialogueEntries ?? promptDialogueEntries
  const dialogueEdges = useMemo(
    () => edges.filter((edge) => edge.target === id && edge.targetHandle === 'in_dialogue_prompt'),
    [edges, id],
  )
  const hasDialogueEdge = dialogueEdges.length > 0
  const voiceProfiles = useMemo(
    (): ConcreteVoiceProfile[] => (
      Array.isArray(data.in_voice_profiles)
        ? data.in_voice_profiles.filter(isConcreteVoiceProfile)
        : []
    ),
    [data.in_voice_profiles],
  )
  const voiceProfileEdges = useMemo(
    () => edges.filter((edge) => edge.target === id && edge.targetHandle === 'in_voice_profiles'),
    [edges, id],
  )
  const hasVoiceProfileEdge = voiceProfileEdges.length > 0

  useEffect(() => {
    voiceProfileSourceLog({
      event: 'voice-profile-source:state',
      node_id: id,
      connected: hasVoiceProfileEdge,
      edge_count: voiceProfileEdges.length,
      profile_count: voiceProfiles.length,
    })
  }, [hasVoiceProfileEdge, id, voiceProfileEdges.length, voiceProfiles.length])
  const voiceRoleOptions = useMemo(() => {
    const roleIds = new Set<string>()
    for (const entry of dialogueEntriesForDisplay) {
      if (entry.voice_role_id) roleIds.add(entry.voice_role_id)
    }
    for (const profile of voiceProfiles) {
      if (profile.name) roleIds.add(profile.name)
    }
    return [...roleIds].sort()
  }, [dialogueEntriesForDisplay, voiceProfiles])

  // History
  const historyEntries = useMemo(
    (): VideoHistoryEntry[] => generated_video?.history ?? [],
    [generated_video?.history],
  )
  const pickedIndex = generated_video?.picked_index ?? 0

  const isRunning = status === 'running'
  const isPendingExecution = status === 'pending_execution'
  const isPendingCost = status === 'pending_cost_confirmation'
  const showSpinner = isRunning || isPendingExecution || isPendingCost
  const spinClass = isRunning ? 'animate-spin' : isPendingCost ? '' : 'animate-spin-slow'
  const batchPending = isRunning ? (data.batch_pending ?? 0) : 0
  const [isCancelling, setIsCancelling] = useState(false)
  const runningMessage = useMemo(() => {
    if (progressMessage) return progressMessage
    return 'Video Generator is rendering your video now'
  }, [progressMessage])
  const markAudioRemixRunning = useCallback((message: string) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? { ...n, data: { ...n.data, status: 'running', progressMessage: message, error: undefined } }
          : n,
      ),
    )
  }, [id, setNodes])
  const handleCancel = useCallback(() => {
    if (!isRunning || !projectId || isCancelling) return
    setIsCancelling(true)
    cancelNodeExecution(projectId, id)
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('cancelNodeExecution failed:', err)
      })
      .finally(() => setIsCancelling(false))
  }, [isRunning, projectId, id, isCancelling])

  useBottomPanelAutoOpen({
    selected,
    isRunning,
    batchPending,
    setBottomPanelOpen,
    setActiveTab: setBottomPanelTab,
    defaultTab: 'config',
  })

  // Reference selector state
  const [isRefSelectorOpen, setIsRefSelectorOpen] = useState(false)
  const handleOpenRefSelector = useCallback((event?: { stopPropagation: () => void }) => {
    event?.stopPropagation()
    setIsRefSelectorOpen(true)
  }, [])

  const currentRefs = useMemo<SelectedReference[]>(() => {
    return buildAssetReferencesFromEdges(getNodes(), edges, id, 'in_images', 'image')
  }, [edges, getNodes, id])

  const handleRefSave = useCallback(
    (refs: SelectedReference[]) => {
      if (canvas) {
        canvas.replacePortEdges(id, 'in_images', buildImageReferenceEdgeUpdates(refs))
      }

      setIsRefSelectorOpen(false)
    },
    [canvas, id],
  )

  // Auto-snap on model change
  useEffect(() => {
    if (model === 'seedance-2.0-image-to-video' || model === 'seedance-2.0-reference-to-video') {
      // Seedance: resolution must be 480p or 720p, duration must be in [4,15]
      if (resolution !== '480p' && resolution !== '720p') {
        updateConfig('in_resolution', '720p')
      }
      const clampedDuration = Math.max(4, Math.min(15, duration))
      if (clampedDuration !== duration) {
        updateConfig('in_duration', clampedDuration)
      }
    } else if (model === 'kling-v3-pro') {
      // Kling: resolution must be 'auto', duration must be in [4,12]
      if (resolution !== 'auto') {
        updateConfig('in_resolution', 'auto')
      }
      const clampedDuration = Math.max(4, Math.min(12, duration))
      if (clampedDuration !== duration) {
        updateConfig('in_duration', clampedDuration)
      }
    } else if (model === 'veo-3.1') {
      // Veo: resolution must be in [720p, 1080p, 4k] (not 'auto')
      const newResolution = (resolution === 'auto' || !['720p', '1080p', '4k'].includes(resolution))
        ? '720p'
        : resolution

      if (newResolution !== resolution) {
        updateConfig('in_resolution', newResolution)
      }

      // Snap duration based on target resolution
      const snappedDuration = snapOnResolutionChange(model, newResolution, duration)
      if (snappedDuration !== duration) {
        updateConfig('in_duration', snappedDuration)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model])

  // Auto-snap duration on resolution change (within Veo or Seedance)
  useEffect(() => {
    if (model !== 'veo-3.1' && model !== 'seedance-2.0-image-to-video' && model !== 'seedance-2.0-reference-to-video') return

    const snappedDuration = snapOnResolutionChange(model, resolution, duration)
    if (snappedDuration !== duration) {
      updateConfig('in_duration', snappedDuration)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolution])

  // Derive current video path from history[picked_index] or fallback.
  // Picked entry wins so the preview follows the Variants picker. The
  // out_video fallback covers library imports that only carry the canonical
  // selected-video pointer. The out_remixed_video legacy fallback only kicks in
  // for nodes whose selected pointer/history hasn't been backfilled yet
  // (pre-2026-04-25 data).
  // TODO(2026-Q3): Remove out_remixed_video fallback after migrator
  // migrate_video_remix_history_backfill has been live for ≥30 days.
  const videoPath = useMemo(() => {
    const selectedHistoryPath = historyEntries[pickedIndex]?.result?.local_path
    if (selectedHistoryPath) return selectedHistoryPath
    if (out_video) return out_video
    if (out_remixed_video) return out_remixed_video
    return undefined
  }, [out_remixed_video, out_video, historyEntries, pickedIndex])

  useEffect(() => {
    setAssetDisplayDimensions(null)
  }, [videoPath, pickedIndex])

  const baseVideoUrl =
    videoPath && projectId
      ? getAssetUrl(projectId, videoPath, 0, data.cacheKey)
      : null

  const videoUrl = useAuthenticatedMediaUrl(baseVideoUrl)

  const originalVideoOptions = useMemo<AudioRemixSourceOption[]>(() => {
    if (!projectId) return []
    return historyEntries.flatMap((entry, index) => {
      if (isAudioRemixHistoryEntry(entry)) return []
      const localPath = entry?.result?.local_path
      if (!localPath) return []
      return [{
        index,
        label: getVariantLabel(historyEntries, index),
        localPath,
        url: getAssetUrl(projectId, localPath, 0, data.cacheKey),
      }]
    })
  }, [data.cacheKey, historyEntries, projectId])

  const selectedAudioSourceOption = useMemo(() => {
    const config = data.in_audio_remix_config
    if (typeof config?.sourceOriginalIndex === 'number') {
      const byIndex = originalVideoOptions.find((option) => option.index === config.sourceOriginalIndex)
      if (byIndex) return byIndex
    }
    if (typeof config?.sourceOriginalPath === 'string') {
      const byPath = originalVideoOptions.find((option) => option.localPath === config.sourceOriginalPath)
      if (byPath) return byPath
    }
    return originalVideoOptions[originalVideoOptions.length - 1] ?? null
  }, [data.in_audio_remix_config, originalVideoOptions])

  // Original video URL (before any remix) — selected original or latest original.
  const origVideoPath = useMemo(() => {
    if (selectedAudioSourceOption?.localPath) return selectedAudioSourceOption.localPath
    if (historyEntries.length > 0) {
      for (let i = historyEntries.length - 1; i >= 0; i--) {
        const e = historyEntries[i]
        const localPath = e?.result?.local_path
        if (localPath && !isAudioRemixHistoryEntry(e)) {
          return localPath
        }
      }
      const firstHistoryPath = historyEntries[0]?.result?.local_path
      if (firstHistoryPath) return firstHistoryPath
    }
    if (out_video) return out_video
    // TODO(2026-Q3): Remove out_remixed_video fallback after migrator
    // migrate_video_remix_history_backfill has been live for ≥30 days.
    if (out_remixed_video) return out_remixed_video
    return undefined
  }, [historyEntries, out_remixed_video, out_video, selectedAudioSourceOption])
  const baseOrigVideoUrl =
    origVideoPath && projectId ? getAssetUrl(projectId, origVideoPath, 0, data.cacheKey) : null
  const origVideoUrl = useAuthenticatedMediaUrl(baseOrigVideoUrl)

  // Build video URL for bottom panel cells
  const buildVideoUrl = useCallback(
    (localPath: string): string | null => {
      if (!projectId) return null
      return getAssetUrl(projectId, localPath)
    },
    [projectId],
  )

  // Round 4 items 4 + 5 + 6: route preset clicks to the right target.
  //  - Freeform editor open  → insertAtCursor on the freeform RichTextBox.
  //  - Structured block editor open → insertAtCursor on that block's body
  //    RichTextBox (via formattedMotionPromptRef.getActiveBodyRef()).
  //  - Structured mode, no editor open → append a new video block whose body
  //    is the preset; the new editor auto-opens via pendingEdit.
  // Every path snapshots the current prompt onto prevPromptStackRef so Cmd+Z
  // can revert (item 6). Item 5 is enforced inside CameraCommandsPanel which
  // now only inserts `[Preset] ` and no `[End of …]` closing tag.
  const updateConfigs = useCallback(
    (updates: Record<string, NodeDataValue>) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...updates } } : n)),
      )
      canvas?.updateNodeData(id, updates)
    },
    [id, setNodes, canvas],
  )

  // Update config
  const updateConfig = useCallback(
    (key: string, value: NodeDataValue) => {
      updateConfigs({ [key]: value })
    },
    [updateConfigs],
  )

  // Round 4 items 4 + 5 + 6: handler used by CameraCommandsPanel. See the
  // big comment above `updateConfigs` for full routing rules.
  const handlePresetApply = useCallback(
    (preset: string) => {
      const snapshot = isEditingPromptRef.current ? localPromptRef.current : prompt
      prevPromptStackRef.current.push(snapshot)
      if (prevPromptStackRef.current.length > 50) prevPromptStackRef.current.shift()

      if (isEditingPromptRef.current) {
        avScriptLog({ event: 'preset-apply', nodeId: id, mode: 'freeform', target: 'freeform', presetId: preset })
        richTextBoxRef.current?.insertAtCursor(`[${preset}] `)
        return
      }

      const activeBody = formattedMotionPromptRef.current?.getActiveBodyRef()
      if (activeBody) {
        avScriptLog({ event: 'preset-apply', nodeId: id, mode: 'structured', target: 'editor', presetId: preset })
        activeBody.insertAtCursor(`[${preset}] `)
        return
      }

      avScriptLog({ event: 'preset-apply', nodeId: id, mode: 'structured', target: 'new-block', presetId: preset })
      formattedMotionPromptRef.current?.appendVideoBlockFromPreset(preset)
    },
    [id, prompt],
  )

  // Round 4 item 6 / Round 5 bug 2: Cmd+Z reverts the last preset insertion
  // whenever this node is the active selection. Previously gated on
  // `bottomPanelOpen`, which is only true after Audio Remix save — so the
  // listener never installed for normal Config-tab preset use. Scope via
  // `selected` so the shortcut only fires for the currently focused node.
  useEffect(() => {
    if (!selected) {
      if (prevPromptStackRef.current.length > 0) {
        avScriptLog({
          event: 'preset-undo-skip-out-of-scope',
          nodeId: id,
          stackLen: prevPromptStackRef.current.length,
        })
      }
      return
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'KeyZ') return
      if (!(e.metaKey || e.ctrlKey)) return
      if (e.shiftKey) return
      if (prevPromptStackRef.current.length === 0) return
      const prev = prevPromptStackRef.current.pop()
      if (prev === undefined) return
      e.preventDefault()
      e.stopPropagation()
      avScriptLog({ event: 'preset-undo', nodeId: id, restoredLen: prev.length })
      if (isEditingPromptRef.current) {
        setLocalPrompt(prev)
      }
      updateConfig('in_motion_prompt', prev)
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [selected, id, updateConfig])

  const handleAudioSourceOriginalChange = useCallback(
    (index: number) => {
      const option = originalVideoOptions.find((candidate) => candidate.index === index)
      if (!option) return
      const currentConfig = data.in_audio_remix_config ?? { groups: [] }
      updateConfig('in_audio_remix_config', {
        ...currentConfig,
        sourceOriginalIndex: option.index,
        sourceOriginalPath: option.localPath,
      })
    },
    [data.in_audio_remix_config, originalVideoOptions, updateConfig],
  )

  const detachDialogueEdges = useCallback(() => {
    if (!hasDialogueEdge) return
    canvas?.replacePortEdges(id, 'in_dialogue_prompt', [])
  }, [canvas, hasDialogueEdge, id])

  const detachVoiceProfileEdges = useCallback(() => {
    if (!hasVoiceProfileEdge) return
    canvas?.replacePortEdges(id, 'in_voice_profiles', [])
  }, [canvas, hasVoiceProfileEdge, id])

  const writeStructuredDialogue = useCallback(
    (entries: DialogueEntry[]) => {
      detachDialogueEdges()
      updateConfig('in_dialogue_prompt', entries)
    },
    [detachDialogueEdges, updateConfig],
  )

  const writeVoiceProfiles = useCallback(
    (profiles: VoiceProfile[]) => {
      detachVoiceProfileEdges()
      updateConfig('in_voice_profiles', profiles)
    },
    [detachVoiceProfileEdges, updateConfig],
  )

  const handleAddDialogueEntry = useCallback(
    (entry: DialogueEntry) => {
      writeStructuredDialogue([...dialogueEntriesForDisplay, entry])
      setShowDialogueForm(false)
    },
    [dialogueEntriesForDisplay, writeStructuredDialogue],
  )

  const handleUpdateDialogueEntry = useCallback(
    (index: number, entry: DialogueEntry) => {
      const nextEntries = [...dialogueEntriesForDisplay]
      nextEntries[index] = entry
      writeStructuredDialogue(nextEntries)
    },
    [dialogueEntriesForDisplay, writeStructuredDialogue],
  )

  const handleDeleteDialogueEntry = useCallback(
    (index: number) => {
      writeStructuredDialogue(dialogueEntriesForDisplay.filter((_, i) => i !== index))
    },
    [dialogueEntriesForDisplay, writeStructuredDialogue],
  )

  const handleResetDialogueFromPrompt = useCallback(() => {
    writeStructuredDialogue(promptDialogueEntries)
    setShowDialogueForm(false)
  }, [promptDialogueEntries, writeStructuredDialogue])

  const handleAddVoiceProfile = useCallback(
    (profile: ConcreteVoiceProfile) => {
      writeVoiceProfiles([...voiceProfiles, profile])
    },
    [voiceProfiles, writeVoiceProfiles],
  )

  const handleUpdateVoiceProfile = useCallback(
    (index: number, profile: ConcreteVoiceProfile) => {
      const nextProfiles = [...voiceProfiles]
      nextProfiles[index] = profile
      writeVoiceProfiles(nextProfiles)
    },
    [voiceProfiles, writeVoiceProfiles],
  )

  const handleDeleteVoiceProfile = useCallback(
    (index: number) => {
      writeVoiceProfiles(voiceProfiles.filter((_, i) => i !== index))
    },
    [voiceProfiles, writeVoiceProfiles],
  )

  const handleMakeVoiceProfilesLocalCopy = useCallback(() => {
    voiceProfileSourceLog({
      event: 'voice-profile-source:make_local_copy',
      node_id: id,
      edge_count: voiceProfileEdges.length,
      profile_count: voiceProfiles.length,
    })
    detachVoiceProfileEdges()
    updateConfig('in_voice_profiles', voiceProfiles)
  }, [detachVoiceProfileEdges, id, updateConfig, voiceProfileEdges.length, voiceProfiles])

  // Rename handler for FloatingNodeLabel
  const handleRename = useCallback(
    (newName: string) => {
      updateConfig('in_name', newName)
    },
    [updateConfig],
  )

  // Generate handler — useClickLock adds 1s cooldown so a fast double-
  // tap can't fire /execute-node twice; InsufficientSparksError catch
  // routes the 402 to the workspace InsufficientSparksModal.
  const showInsufficientSparks = useShowInsufficientSparks()
  const { isLocked: isGenerateLockedKey, run: runGenerate } = useClickLock(1000)
  const generateLocked = isGenerateLockedKey(id)

  const handleGenerate = useCallback(async (retryCount: number = 1) => {
    if (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation') return

    // Auto-save unsaved motion prompt edits before generating
    if (isEditingPromptRef.current) {
      updateConfig('in_motion_prompt', localPromptRef.current)
      setIsEditingPrompt(false)
    }

    if (!projectId) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: 'Missing projectId.' } }
            : n,
        ),
      )
      return
    }

    void runGenerate(id, async () => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'running', progress: 0, action: undefined, progressMessage: undefined, error: undefined } }
            : n,
        ),
      )

      try {
        await executeNode(projectId, id, undefined, retryCount)
      } catch (err) {
        if (err instanceof InsufficientSparksError) {
          showInsufficientSparks({ balance: err.balance, required: err.required, nodeId: id })
          setNodes((nds) =>
            nds.map((n) =>
              n.id === id
                ? { ...n, data: { ...n.data, status: 'idle', error: undefined } }
                : n,
            ),
          )
          return
        }
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
              : n,
          ),
        )
      }
    })
  }, [projectId, id, status, setNodes, runGenerate, showInsufficientSparks, updateConfig])

  // Download handler — follows the picker so users get the variant they selected.
  // TODO(2026-Q3): Remove out_remixed_video fallback after migrator
  // migrate_video_remix_history_backfill has been live for ≥30 days.
  const downloadPath = useMemo(() => {
    const selectedHistoryPath = historyEntries[pickedIndex]?.result?.local_path
    if (selectedHistoryPath) return selectedHistoryPath
    if (out_video) return out_video
    if (out_remixed_video) return out_remixed_video
    return undefined
  }, [out_remixed_video, out_video, historyEntries, pickedIndex])

  const onDownload = useCallback(() => {
    if (!downloadPath) return
    const { filename } = buildClipDownloadFilename({
      canvasNodes: getNodes(),
      canvasEdges: getEdges(),
      nodeId: id,
      variantHistory: historyEntries,
      pickedIndex,
      assetPath: downloadPath,
    })
    downloadFile(downloadPath, filename)
  }, [downloadPath, downloadFile, getNodes, getEdges, id, historyEntries, pickedIndex])

  // Handle history selection
  const handleHistorySelect = useCallback(
    (index: number) => {
      const entry = historyEntries[index]
      const localPath = entry?.result?.local_path
      if (!localPath) return

      const currentNode = getNodes().find((n) => n.id === id)
      const currentGenVideo = currentNode
        ? getTypedNodeData(currentNode, 'videoGenerator')?.out_generated_video ?? out_generated_video
        : out_generated_video
      const newCacheKey = String(Date.now())
      const updatedGenVideo = currentGenVideo
        ? { ...currentGenVideo, picked_index: index }
        : { history: historyEntries, picked_index: index }

      setNodes((nds) =>
        nds.map((n) =>
          n.id !== id
            ? n
            : {
                ...n,
                data: {
                  ...n.data,
                  out_generated_video: updatedGenVideo,
                  cacheKey: newCacheKey,
                },
              },
        ),
      )
      canvas?.updateNodeData(id, {
        out_generated_video: updatedGenVideo,
        cacheKey: newCacheKey,
      })
    },
    [id, historyEntries, getNodes, setNodes, canvas, out_generated_video],
  )



  // Audio remix: save config to YDoc and execute remix
  const handleSaveChangesWithConfig = useCallback(async (config: AudioRemixConfig) => {
    if (!projectId || isSavingRemix) return
    setIsSavingRemix(true)
    const configWithSource: AudioRemixConfig = selectedAudioSourceOption
      ? {
          ...config,
          sourceOriginalIndex: selectedAudioSourceOption.index,
          sourceOriginalPath: selectedAudioSourceOption.localPath,
        }
      : config

    const tracksWithSpeed = configWithSource.groups.flatMap(g => g.tracks).filter(t => t.sourceDurationS != null && Math.abs((t.sourceDurationS / Math.max(t.endS - t.startS, 0.01)) - 1.0) > 0.01)
    audioRemixLog('save-config: groups=%d tracksWithSpeed=%d sourceOriginalIndex=%s', configWithSource.groups.length, tracksWithSpeed.length, configWithSource.sourceOriginalIndex ?? 'latest')

    try {
      // Write remix config to YDoc before executing
      canvas?.updateNodeData(id, { in_audio_remix_config: configWithSource })
      markAudioRemixRunning('Audio remixing: applying mix...')
      await executeNode(projectId, id, 'apply_audio_remix', undefined, {
        in_audio_remix_config: configWithSource,
      })
    } catch (err) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, error: `Remix failed: ${err}` } }
            : n,
        ),
      )
    } finally {
      setIsSavingRemix(false)
    }
  }, [projectId, id, isSavingRemix, selectedAudioSourceOption, setNodes, canvas, markAudioRemixRunning])

  const handleRegenerateAudioRemix = useCallback(() => {
    if (!projectId || !hasGeneratedVideo || showSpinner) return
    markAudioRemixRunning('Audio remixing: regenerating dialogue TTS...')
    void executeNode(projectId, id, 'generate_dialogue_tts').catch((err) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: `TTS generation failed: ${err}` } }
            : n,
        ),
      )
    })
  }, [projectId, hasGeneratedVideo, showSpinner, markAudioRemixRunning, id, setNodes])

  // Toolbar actions
  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'addToChat',
        label: 'Add to Chat',
        icon: <MessageSquarePlus size={14} />,
        onClick: handleAddToChat,
      },
      {
        key: 'save-to-library',
        label: 'Save to Library',
        icon: <BookmarkPlus className="w-4 h-4" />,
        onClick: handleSaveToLibrary,
        group: true,
      },
      {
        key: 'duplicate',
        label: 'Duplicate',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ),
        onClick: handleDuplicate,
        group: true,
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [handleDuplicate, handleDelete, handleAddToChat, handleSaveToLibrary],
  )

  // Reference image picker for Config tab
  const referenceImagePicker = useMemo(() => {
    return (
      <div>
        <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1">
          References
        </label>
        <div className="flex flex-wrap gap-1">
          {currentRefs.map((ref, i) => (
            <div key={`${ref.nodeId}-${i}`} className="relative group/ref">
              <div className="w-10 h-10 bg-surface-1 rounded border border-border flex items-center justify-center overflow-hidden cursor-pointer" onClick={handleOpenRefSelector} title={model === 'kling-v3-pro' && i === 0 ? 'Start Frame' : 'Reference'}>
                <img
                  src={getThumbnailUrl(projectId ?? '', ref.assetPath, 80) ?? undefined}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
              </div>
              <button
                onClick={() => {
                  if (canvas) {
                    const remaining = currentRefs.filter((_, idx) => idx !== i)
                    canvas.replacePortEdges(id, 'in_images', buildImageReferenceEdgeUpdates(remaining))
                  }
                }}
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-xxs flex items-center justify-center opacity-0 group-hover/ref:opacity-100 transition-opacity"
              >
                x
              </button>
            </div>
          ))}
          <button
            onClick={handleOpenRefSelector}
            className="w-10 h-10 rounded border border-dashed border-border-subtle hover:border-primary/50 text-foreground/30 hover:text-primary/60 flex items-center justify-center transition-colors"
            title="Add reference images"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </button>
        </div>
      </div>
    )
  }, [currentRefs, projectId, model, canvas, id, handleOpenRefSelector])

  // Build character names from current canvas nodes for the dialogue form.
  // Avoid memoizing getNodes(); its function identity is stable while node data can change.
  const characterNames = Array.from(new Set(
    getNodes()
      .filter((n) => n.type === 'characterDesigner')
      .map((n) => {
        const d = getTypedNodeData(n, 'characterDesigner')
        return (d?.in_name || d?.out_name || '').trim()
      })
      .filter(Boolean),
  ))
  const ttsSegmentCount = Array.isArray(out_tts_segments) ? out_tts_segments.length : 0

  // Build motion prompt + error content for bottom panel.
  // CINEMATOGRAPHY PRESETS are rendered whenever the user is editing the
  // motion prompt — either via the freeform RichTextBox (isEditingPrompt) or
  // via any Structured per-block editor (isStructuredEditing, lifted up from
  // FormattedMotionPrompt). The buttons insert tags into the most-recently
  // focused editor (existing RichTextBox + native textarea behavior).
  const showCinematographyPresets = isEditingPrompt || isStructuredEditing
  const motionPromptContent = useMemo(() => {
    return (
      <>
        <div>
          {isEditingPrompt ? (
            <>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs uppercase tracking-wider text-text-muted">
                  Motion Prompt
                </label>
                <button
                  onClick={() => {
                    updateConfig('in_motion_prompt', localPrompt)
                    setIsEditingPrompt(false)
                  }}
                  className="w-5 h-5 rounded hover:bg-surface-1 text-primary/80 hover:text-primary flex items-center justify-center transition-colors"
                  title="Done editing"
                  data-testid="motion-prompt-done-btn"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                </button>
              </div>
              <RichTextBox
                ref={richTextBoxRef}
                containerClassName="w-full h-56 bg-surface-1 border border-border rounded px-2 py-1.5 nowheel"
                containerTestId="motion-prompt-textarea"
                canvasNodes={getNodes()}
                placeholder="Describe the video action and motion..."
                initialContent={localPrompt}
                onContentChange={(state: RichTextBoxState) => setLocalPrompt(state.plainText)}
                minHeight={200}
                maxHeight={220}
                maxLength={CREATIVE_TEXT_MAX_LENGTH}
                onSubmit={() => {
                  updateConfig('in_motion_prompt', localPrompt)
                  setIsEditingPrompt(false)
                }}
                onEscape={() => {
                  setLocalPrompt(prompt)
                  setIsEditingPrompt(false)
                }}
              />
            </>
          ) : (
            <FormattedMotionPrompt
              ref={formattedMotionPromptRef}
              text={prompt}
              nodeId={id}
              onEdit={() => setIsEditingPrompt(true)}
              onCommitStructured={(newText) => updateConfig('in_motion_prompt', newText)}
              canvasNodes={getNodes()}
              projectId={projectId ?? ''}
              onEditingChange={setIsStructuredEditing}
            />
          )}
        </div>

        {/* Error Banner */}
        {error && (
          <div className="p-2 bg-red-500/10 rounded border border-red-500/30" data-testid="error-banner">
            <span className="text-xxs text-red-400 cursor-help line-clamp-2" title={error}>
              {sanitizeNodeError(error)}
            </span>
          </div>
        )}

        {/* CINEMATOGRAPHY PRESETS — always visible at the bottom of the panel
            whenever ANY motion-prompt editor is active (freeform OR structured
            per-block). Round 4 item 4: the `onInsert` handler routes the
            preset to the right target (freeform editor / open block editor /
            new video block). */}
        {showCinematographyPresets && (
          <CameraCommandsPanel
            richTextBoxRef={richTextBoxRef}
            onInsert={handlePresetApply}
          />
        )}
      </>
    )
  }, [
    isEditingPrompt,
    showCinematographyPresets,
    localPrompt,
    updateConfig,
    prompt,
    error,
    getNodes,
    projectId,
    id,
    handlePresetApply,
  ])

  const audioRemixContent = useMemo(() => {
    return (
      <div className="flex h-full min-h-0 flex-col nowheel" data-testid="audio-remix-panel">
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          <div className="rounded-lg border border-border-subtle bg-surface-0 p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-text-muted">Audio Remix</label>
                <p className="mt-1 text-xxs text-text-muted">
                  Dialogue is extracted from the motion prompt and can be edited below before generating speech tracks.
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={handleRegenerateAudioRemix}
                  disabled={!hasGeneratedVideo || showSpinner}
                  className="flex h-8 items-center gap-1.5 rounded-lg border border-input-border bg-surface-1 px-3 text-xs font-medium text-foreground transition-colors hover:bg-surface-3 hover:border-input-border-hover disabled:cursor-not-allowed disabled:border-input-border disabled:bg-surface-1 disabled:text-text-muted disabled:hover:bg-surface-1 disabled:hover:border-input-border focus:outline-none focus:ring-2 focus:ring-primary/20"
                  data-testid="regen-audio-remix-btn"
                  title={hasGeneratedVideo ? 'Regenerate speech tracks from the current dialogue' : 'Generate a video first'}
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  {ttsSegmentCount > 0 ? 'Regenerate TTS' : 'Generate TTS'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsAudioEditorOpen(true)}
                  disabled={!hasGeneratedVideo}
                  className="h-8 rounded-lg border border-primary/40 bg-primary/20 px-3 text-xs font-medium text-primary transition-colors hover:bg-primary/30 hover:border-primary/60 disabled:cursor-not-allowed disabled:border-input-border disabled:bg-surface-1 disabled:text-text-muted disabled:hover:bg-surface-1 disabled:hover:border-input-border focus:outline-none focus:ring-2 focus:ring-primary/20"
                  data-testid="audio-editor-btn"
                  title={hasGeneratedVideo ? 'Open Audio Editor' : 'Generate a video first to open the audio editor'}
                >
                  Audio Editor
                </button>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              <span className="rounded-full border border-input-border bg-surface-1 px-1.5 py-0.5 text-xxs text-text-muted">
                {hasStoredDialogue ? 'Manual dialogue' : promptDialogueEntries.length > 0 ? 'Parsed from prompt' : 'No dialogue'}
              </span>
              {hasDialogueEdge && (
                <span className="rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-xxs text-primary">
                  connected
                </span>
              )}
              <span className="rounded-full border border-input-border bg-surface-1 px-1.5 py-0.5 text-xxs text-text-muted">
                {ttsSegmentCount > 0 ? `${ttsSegmentCount} TTS segment${ttsSegmentCount === 1 ? '' : 's'}` : 'TTS not generated'}
              </span>
            </div>

            {originalVideoOptions.length > 0 && (
              <div
                className="mt-3 rounded-lg border border-input-border bg-input-bg p-2.5"
                data-testid="audio-remix-panel-base-video-preview"
              >
                <div className="flex items-stretch gap-3">
                  <div className="relative aspect-video w-32 shrink-0 overflow-hidden rounded-lg border border-border bg-background">
                    {origVideoUrl ? (
                      <video
                        src={origVideoUrl}
                        muted
                        playsInline
                        preload="metadata"
                        className="h-full w-full object-cover"
                        data-testid="audio-remix-panel-base-video-thumb"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-xxs text-text-muted">
                        No preview
                      </div>
                    )}
                    {selectedAudioSourceOption?.label && (
                      <div className="absolute inset-x-0 bottom-0 bg-background/80 px-2 py-1">
                        <span
                          className="block truncate text-xxs font-medium text-foreground"
                          data-testid="audio-remix-panel-base-video-label"
                        >
                          {selectedAudioSourceOption.label}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <label
                        htmlFor={baseVideoSelectId}
                        className="text-xs font-medium uppercase tracking-wider text-text-muted"
                      >
                        Base video
                      </label>
                      <span className="shrink-0 rounded-full border border-border bg-surface-1 px-1.5 py-0.5 text-xxs text-text-muted">
                        {originalVideoOptions.length} original{originalVideoOptions.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <select
                      id={baseVideoSelectId}
                      aria-label="Base video"
                      value={selectedAudioSourceOption?.index ?? ''}
                      onChange={(event) => handleAudioSourceOriginalChange(Number(event.target.value))}
                      className="h-8 w-full min-w-0 rounded-lg border border-input-border bg-surface-1 px-2 text-xs text-foreground outline-none transition-colors hover:border-input-border-hover hover:bg-surface-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
                      data-testid="audio-remix-panel-source-original-select"
                    >
                      {originalVideoOptions.map((option) => (
                        <option key={option.index} value={option.index}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {!hasGeneratedVideo && (
              <p className="mt-2 rounded-lg border border-warning/20 bg-warning/10 px-2 py-1 text-xxs text-warning/90">
                Generate a video before opening the audio editor. Dialogue can still be edited here.
              </p>
            )}
          </div>

          <VoiceProfilesPanel
            profiles={voiceProfiles}
            suggestedRoleIds={voiceRoleOptions}
            provider={ttsProvider}
            readOnly={hasVoiceProfileEdge}
            readOnlyMessage="Connected voice profiles are read-only. Change the source Character Designer or Script Writer voice profile, or make a local copy for this video."
            onMakeLocalCopy={hasVoiceProfileEdge ? handleMakeVoiceProfilesLocalCopy : undefined}
            onAdd={handleAddVoiceProfile}
            onUpdate={handleUpdateVoiceProfile}
            onDelete={handleDeleteVoiceProfile}
          />

          <DialoguePromptPanel
            entries={dialogueEntriesForDisplay}
            promptEntries={promptDialogueEntries}
            hasStoredDialogue={hasStoredDialogue}
            hasIncomingEdge={hasDialogueEdge}
            showDialogueForm={showDialogueForm}
            duration={duration}
            characterNames={characterNames}
            voiceRoleOptions={voiceRoleOptions}
            clipIndex={clipIndex}
            onShowForm={() => setShowDialogueForm(true)}
            onAdd={handleAddDialogueEntry}
            onUpdate={handleUpdateDialogueEntry}
            onDelete={handleDeleteDialogueEntry}
            onCancelAdd={() => setShowDialogueForm(false)}
            onResetFromPrompt={handleResetDialogueFromPrompt}
          />
        </div>
      </div>
    )
  }, [
    characterNames,
    clipIndex,
    dialogueEntriesForDisplay,
    duration,
    handleAddDialogueEntry,
    handleDeleteDialogueEntry,
    handleAddVoiceProfile,
    handleDeleteVoiceProfile,
    handleMakeVoiceProfilesLocalCopy,
    handleResetDialogueFromPrompt,
    handleRegenerateAudioRemix,
    handleAudioSourceOriginalChange,
    handleUpdateDialogueEntry,
    handleUpdateVoiceProfile,
    baseVideoSelectId,
    hasDialogueEdge,
    hasGeneratedVideo,
    hasStoredDialogue,
    hasVoiceProfileEdge,
    originalVideoOptions,
    origVideoUrl,
    promptDialogueEntries,
    selectedAudioSourceOption,
    showDialogueForm,
    showSpinner,
    ttsProvider,
    ttsSegmentCount,
    voiceProfiles,
    voiceRoleOptions,
  ])

  return (
    <div className="relative">
      <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

      {/* Floating label (standalone mode only) */}
      {!is_inside_group && (
        <FloatingNodeLabel
          name={in_name || label || ''}
          fallback="Video Generator"
          typeIcon={NODE_TYPE_ICON.videoGenerator}
          onRename={handleRename}
        />
      )}

      <div
        className={`rounded-lg shadow transition-all bg-card border group relative ${
          selected ? 'ring-2 ring-white/70 border-white/40' : 'border-border'
        }`}
        style={{ width: nodeWidth }}
      >
        {/* Connection Handles */}
        <Handle
          type="target"
          position={Position.Top}
          id="in_images"
          className="!bg-transparent !border-0 !w-4 !h-1"
        />
        <Handle
          type="target"
          position={Position.Left}
          id="in_audio"
          className="!bg-transparent !border-0 !w-1 !h-4"
          style={{ top: '20%' }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="in_dialogue_prompt"
          className="!bg-transparent !border-0 !w-1 !h-4"
          style={{ top: '50%' }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="in_voice_profiles"
          className="!bg-transparent !border-0 !w-1 !h-4"
          style={{ top: '85%' }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="out_generated_video"
          className="!bg-transparent !border-0 !w-4 !h-1"
          style={{ left: '30%' }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="out_remixed_video"
          className="!bg-transparent !border-0 !w-4 !h-1"
          style={{ left: '70%' }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="out_video"
          className="!bg-transparent !border-0 !w-4 !h-1"
          style={{ left: '50%' }}
        />

        {/* Preview Area — all 4 corners rounded */}
        <div
          className="relative w-full bg-canvas overflow-hidden cursor-pointer group rounded-lg"
          style={{ aspectRatio: cssAspectRatio }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          data-testid="video-preview-area"
        >
          {showSpinner ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
              <div className={`w-8 h-8 border-2 border-[var(--warning)] border-t-transparent rounded-full ${spinClass}`} />
              <span className="text-xxs text-foreground/50 mt-2 text-center leading-relaxed">
                {isRunning
                  ? runningMessage
                  : isPendingCost
                    ? 'Waiting for cost approval in chat...'
                    : 'Queued — waiting for upstream nodes...'}
              </span>
              {isRunning && (
                <span className="text-xxs text-foreground/25 mt-1">This may take up to 2 minutes</span>
              )}
              {isRunning && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleCancel() }}
                  disabled={isCancelling}
                  className="mt-3 flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors nodrag"
                  data-testid="cancel-node-button"
                  title="Stop"
                >
                  <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="6" width="12" height="12" rx="1" />
                  </svg>
                  <span className="text-xxs font-medium">Stop</span>
                </button>
              )}
            </div>
          ) : videoUrl ? (
            <>
              <VideoPreviewPlayer
                videoUrl={videoUrl}
                isHovering={isHovering}
                showExpand
                showDownload
                onExpand={() => setIsVideoModalOpen(true)}
                onDownload={onDownload}
                onLoadedDimensions={(width, height) => {
                  setAssetDisplayDimensions(snapNaturalDimensionsToDisplayDimensions(width, height))
                }}
              />
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-foreground/20">
              <svg className="w-10 h-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              <span className="text-xxs">No video yet</span>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Panel */}
      {/* minWidth=960 leaves comfortable room for 3 structured columns
          (TimeRuler + Video + Dialogue/SFX) at the default zoom without
          squeezing dialogue text. ShortVideoNode does not use this component
          so the wider min only affects VideoGenerator. */}
      <UnifiedBottomPanel
        contentHeight={520}
        minWidth={960}
        tabs={videoTabs}
        activeTab={bottomPanelTab}
        onTabChange={setBottomPanelTab}
        isVisible={!!selected && !dragging && bottomPanelOpen }
      >
        {(tab) => {
          if (tab === 'config') {
            return (
              <VideoConfigTabContent
                model={model}
                onModelChange={(m) => updateConfig('in_model', m)}
                referenceImagePicker={referenceImagePicker}
                motionPromptContent={motionPromptContent}
                duration={duration}
                resolution={resolution}
                aspectRatio={aspect_ratio}
                generateAudio={generate_audio}
                ttsProvider={ttsProvider}
                onDurationChange={(d) => updateConfig('in_duration', d)}
                onResolutionChange={(r) => updateConfig('in_resolution', r)}
                onAspectRatioChange={(r) => updateConfig('in_aspect_ratio', r)}
                onGenerateAudioChange={(a) => updateConfig('in_generate_audio', a)}
                onTtsProviderChange={(provider) => {
                  if (provider === ttsProvider) return
                  detachVoiceProfileEdges()
                  updateConfigs({ in_tts_provider: provider, in_voice_profiles: [] })
                }}
                dialogueMode={dialogueMode}
                onDialogueModeChange={(mode) => updateConfig('in_dialogue_mode', mode)}
                isRunning={isRunning || generateLocked}
                onGenerate={handleGenerate}
                onCancel={handleCancel}
                isCancelling={isCancelling}
                activeTab={bottomPanelTab}
                canGenerate={motionCanGenerate}
                cannotGenerateReason={motionGenerateBlockReason}
              />
            )
          }
          if (tab === 'variants') {
            return (
              <VideoVariantsTabContent
                history={historyEntries}
                pickedIndex={pickedIndex}
                onSelectVariant={handleHistorySelect}
                batchPending={batchPending}
                buildVideoUrl={buildVideoUrl}
                onDownloadVariant={(path, realIdx) => {
                  const { filename } = buildClipDownloadFilename({
                    canvasNodes: getNodes(),
                    canvasEdges: getEdges(),
                    nodeId: id,
                    variantHistory: historyEntries,
                    pickedIndex: realIdx,
                    assetPath: path,
                  })
                  downloadFile(path, filename)
                }}
                defaultAspectRatio={cssAspectRatio}
                nodeId={id}
              />
            )
          }
          if (tab === 'audio') {
            return audioRemixContent
          }
          return null
        }}
      </UnifiedBottomPanel>

      {/* Reference Selector modal */}
      {isRefSelectorOpen && (
        <ReferenceSelector
          isOpen={isRefSelectorOpen}
          onClose={() => setIsRefSelectorOpen(false)}
          onSave={handleRefSave}
          excludeNodeId={id}
          canvasNodes={canvas?.getNodes() ?? getNodes()}
          currentReferences={currentRefs}
          maxReferences={9}
          targetBackendType="video_generator"
          onUploadFile={uploadFile ?? undefined}
        />
      )}

      {/* Video Preview Lightbox */}
      {isVideoModalOpen &&
        createPortal(
          <div
            data-testid="video-lightbox-backdrop"
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setIsVideoModalOpen(false)}
          >
            <div
              data-testid="video-lightbox"
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="video-lightbox-close"
                onClick={() => setIsVideoModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <video
                src={videoUrl ?? undefined}
                controls
                autoPlay
                className="max-w-full max-h-[85vh] rounded-lg shadow-2xl bg-black"
              />
            </div>
          </div>,
          document.body,
        )}

      {/* Audio Remix Modal */}
      {isAudioEditorOpen && projectId &&
        createPortal(
          <AudioEditorModal
            projectId={projectId}
            videoNodeName={in_name || label || null}
            origVideoUrl={origVideoUrl}
            sourceOptions={originalVideoOptions}
            selectedSourceIndex={selectedAudioSourceOption?.index ?? null}
            videoDuration={videoDuration}
            segments={out_tts_segments ?? []}
            dialogueEntries={data.in_dialogue_prompt ?? null}
            config={data.in_audio_remix_config ?? null}
            baseline={data.out_audio_remix_baseline ?? null}
            isSavingRemix={isSavingRemix}
            onConfigChange={(config) => {
              canvas?.updateNodeData(id, { in_audio_remix_config: config })
            }}
            onSave={(config) => {
              audioRemixLog('save-and-remix: groups=%d', config.groups.length)
              handleSaveChangesWithConfig(config)
              setIsAudioEditorOpen(false)
              setBottomPanelTab('variants')
              setBottomPanelOpen(true)
            }}
            onClose={() => { audioRemixLog('modal-close'); setIsAudioEditorOpen(false) }}
            onRerunSTT={() => {
              audioRemixLog('rerun-stt: executing run_voice_acting')
              if (projectId) {
                markAudioRemixRunning('Audio remixing...')
                executeNode(projectId, id, 'run_voice_acting')
              }
              setIsAudioEditorOpen(false)
            }}
            onGenerateDialogue={() => {
              audioRemixLog('generate-dialogue-tts: executing generate_dialogue_tts')
              if (projectId) {
                handleRegenerateAudioRemix()
              }
              setIsAudioEditorOpen(false)
            }}
          />,
          document.body,
        )}
    </div>
  )
})

VideoGeneratorNode.displayName = 'VideoGeneratorNode'
